/*
 * sb-3d.js — Shimmerballs configurator: real-time 3D preview.
 *
 * Replaces the 2D spherical warp (assets/sb-warp-preview.js, retired) with a
 * WebGL scene. Exposes a `window.SB3D` singleton so sb-configurator.js keeps
 * its existing call shape — in particular renderExport() still resolves to a
 * canvas, which is what lets addToBasket() stay untouched.
 *
 * Build phase 2: geometry is a plain grey sphere. Environment/PMREM, gores,
 * seams, hardware, artwork and the material table land in later phases; their
 * entry points exist here already so the wiring does not change again.
 *
 * three.js is resolved through the theme importmap ("three"), declared in
 * snippets/scripts.liquid. No bundler, no CDN — see brief §3.
 */

import * as THREE from 'three';

/* ------------------------------------------------------------------ config */

const CAM_FOV = 32;
const BALL_RADIUS = 1; // world units. Physical diameter is carried separately.

// Vertical orbit limits, degrees from the equator. Enough to see the top
// hardware, never enough to find a pole-on angle.
const ELEV_MIN = -20;
const ELEV_MAX = 45;

/*
 * Elastic edges on the orbit limits, instead of a dead stop.
 *
 * `overshootDeg` is how far past a limit the view can ever appear to travel —
 * an asymptote, not a second limit, so no amount of dragging punches through
 * it. The resistance builds the whole way, which is what makes it read as
 * something pushing back rather than something broken.
 *
 * `rawOvershootFactor` caps the UNDISPLAYED value so a long drag cannot bank up
 * an enormous overshoot that then takes seconds to unwind on release.
 */
/*
 * Ground plane — the scale reference.
 *
 * The preview's problem was never perspective. The ball is framed to the same
 * 77% of the viewport at every size, so its on-screen size is CONSTANT from
 * 40cm to 300cm; the only things that change are the hardware, which shrinks
 * 7.5x across the range, and the gore count. With nothing in frame whose real
 * size the viewer already knows, the eye anchors on the biggest, steadiest
 * object — the ball — treats it as the ruler, and concludes the fittings got
 * smaller. The image carries no scale information at all.
 *
 * A grid at a FIXED REAL-WORLD spacing supplies the missing reference. The ball
 * spans 0.8 squares at 40cm and 6 at 300cm, and counting squares is a far more
 * robust judgement than comparing two objects for size.
 *
 * `gridCm` is deliberately 50 rather than 100: at a metre the smallest balls sit
 * inside a single square and there is nothing to count.
 */
const GROUND = {
  gridCm: 50,
  /*
   * The grid is a reference, not a subject: it only has to be countable, and it
   * must not compete with a mirror-finish product for attention. Landed by
   * bracketing — against the preview panel's own light grey these give a line
   * contrast of roughly 18.5 (0xc9ced4 at 0.42, too heavy), 6.6 (0xdfe3e7 at
   * 0.30, too faint to count), and 13 here.
   */
  colour: 0xd2d7dc,
  gridOpacity: 0.38,
  fadeRadius: 9.0, // world units — where the grid dissolves, so it has no edge
  shadowOpacity: 0.34,
  /* Hanging balls sit off the floor, so the shadow spreads and softens and the
   * floor drops away. Both in world units against BALL_RADIUS 1. */
  hangHeight: 1.15,
  hangShadowSpread: 1.9,
  hangShadowFade: 0.45,
  /* How long a size change takes to play out. Long enough to read as growth,
   * short enough not to feel like waiting for the preview. */
  growMs: 480,
};

/*
 * Size change, in two beats: the ball grows, then the camera pulls back to fit
 * it. Zac's brother's suggestion, 27 Aug, and it is a better idea than the
 * symmetric wiggle it replaces.
 *
 * Why it works where a wiggle did not. A wiggle is symmetric, so it reads as a
 * pulse — something jiggled. This is causal. In the first beat the ball swells
 * while the grid is HELD at its old spacing, so it grows against a fixed
 * reference and there is nothing else the growth could be attributed to. In the
 * second beat everything settles together — the ball eases back, the grid
 * animates to its new finer spacing, and the camera dollies out and returns —
 * which the eye reads as the camera retreating to fit a bigger object, not as
 * the ball shrinking again.
 *
 * Holding the grid through beat one is the whole trick. Animating ball and grid
 * together is what made the first attempt read as "the squares got smaller".
 *
 * `swellPower` compresses the real ratio hard: 40cm to 300cm is 7.5x, and a
 * literal 7.5x swell would fill the viewport several times over. The clamps
 * matter more than the exponent.
 */
const GROW = {
  swellPower: 0.32,
  /*
   * The ceiling is set by the frame, not by taste. The ball rests at 75.8% of
   * frame height and `VIEWS.default.targetY` lifts it 3.8% above centre, so its
   * top edge sits 8% down. Swelling by S moves that edge to 46% - 37.9%*S:
   * 1.22 puts it at exactly 0.0 and the ball is clipped, which is what made the
   * first pass measure a 7% swell when 22% was asked for. 1.13 leaves 3.4%.
   *
   * It does not need to be large. The grid is held still through beat one, so
   * the growth is judged against a fixed reference rather than against the
   * frame, and 13% against a static grid is unmistakable.
   */
  swellMax: 1.13,
  swellMin: 0.87,
  growMs: 320, // beat one: the ball swells, grid held
  settleMs: 520, // beat two: everything resolves
};

const RUBBER = {
  elevOvershootDeg: 9,
  azOvershootDeg: 12,
  rawOvershootFactor: 3,
  springBack: 0.16, // per frame, once the pointer is off
};

/*
 * Asymptotic resistance curve: at `maxOver` past the edge the view has moved
 * half of `maxOver`, and it approaches `maxOver` but never reaches it.
 */
function rubberBand(value, lo, hi, maxOver) {
  if (maxOver <= 0) return clamp(value, lo, hi);
  if (value > hi) {
    const over = value - hi;
    return hi + (maxOver * over) / (over + maxOver);
  }
  if (value < lo) {
    const over = lo - value;
    return lo - (maxOver * over) / (over + maxOver);
  }
  return value;
}

/*
 * Zoom limits as camera distance in ball radii. At CAM_FOV the visible height
 * is 2*d*tan(fov/2), so the ball (diameter 2) fills the frame at d = 1/tan(16°)
 * = 3.49. Default sits further out than that to leave breathing room; the inner
 * limit tightens with diameter, because a larger ball carries more real print
 * resolution and survives a closer look before the artwork raster or the
 * environment map gives out.
 */
const DIST_MAX = 7.5; // ball ~47% of frame height
const DIST_MIN_SMALL = 3.4; // 40cm — ball just fills the frame
const DIST_MIN_LARGE = 2.6; // 300cm — closer, for inspecting print detail
const DIST_DEFAULT = 4.6; // ball ~76% of frame height

// Panel counts. 8/12/16 is our assumption, not factory-confirmed (brief §15).
// Seam detection on the reference render measured ~45 degrees of spacing,
// i.e. 8 gores, on whichever size that ball was.
function goresForSize(cm) {
  if (cm <= 60) return 8;
  if (cm <= 120) return 12;
  return 16;
}

/*
 * Pole cap angular radius, measured from the pole. Drives maxLogoPct(), so it
 * is geometry rather than a section setting.
 *
 * Taken from the close-up of a real 80cm ball: the cap seam measures ~43cm
 * across, i.e. a 21.6cm radius on a 40cm-radius ball = 31 degrees. Corroborated
 * by the ring tabs, which sit 15cm from the valve (21.5 degrees) and are
 * plainly INSIDE the cap in the photo.
 *
 * This deliberately contradicts the Silver CGI render, whose strongest circular
 * feature sits at 14 degrees. The two cannot be reconciled — a constant 21.6cm
 * cap would imply the render is a ~177cm ball, but that render measures 8
 * gores, which the size table assigns to 40-60cm. The marketing renders simply
 * do not match the product, so the real ball wins.
 *
 * [UNCONFIRMED] Whether the cap subtends a constant ANGLE across all eleven
 * sizes, or is a constant physical size, is unknown — one real ball cannot
 * distinguish them. Constant angle is assumed because gore counts change with
 * size, implying the pattern is redrawn per size rather than scaled.
 */
const CAP_ANGLE_DEG = 30;

/*
 * Seam rendering. Seams are a specular discontinuity — a welded ridge catching
 * light slightly differently — never a drawn stroke. Drawn into the colour map
 * they would read as a decal on a chrome ball, which is the one thing this
 * renderer must not do. So they live purely in the normal map.
 *
 * The map is one gore wide and repeated, which buys high angular resolution for
 * very little memory: the pattern is periodic in longitude and the cap ring is
 * constant in it. The ridge sits down the tile centre, so with repeat = N the
 * gore centres land on u = k/N — and since N is always divisible by 4, the
 * camera-facing longitude (u = 0.25) is always a gore centre. That matches the
 * reference, where seams measure +/-22-24 degrees either side of the artwork.
 */
const SEAM = {
  // 1024 keeps a thin ridge properly resolved: at 8 gores the tile spans 0.785
  // rad, so 512 would put sigma at only ~1.4 texels and the seam would alias.
  tileWidth: 1024,
  tileHeight: 1024,
  sigmaRad: 0.0021, // ridge half-width, radians of surface arc — sleek, not chunky
  amplitude: 0.46, // normal tilt on the flanks
  ringSigmaRad: 0.0038, // welded ring where gores meet the cap
  // The cap seam is the most prominent single feature on the real ball — a
  // continuous bright circle. It carries more than the pole-to-pole seams do.
  ringAmplitude: 1.1,
  /*
   * Calibrated against the Silver reference, not picked by eye. Measuring the
   * sharpness of thin luminance steps across the smooth lower band of the ball
   * gives a median of 29.4 in the reference; at normalScale 1.0 ours was 5.9,
   * roughly five times too faint. Judging seam strength by eye is unreliable
   * because the bright truss reflections dominate the impression.
   */
  normalScale: 2.5,
};

/*
 * Derivative of a Gaussian: the slope profile of a smooth ridge. Using the
 * analytic derivative rather than a Sobel pass over a height map keeps the
 * flanks clean and free of quantisation stair-stepping.
 */
const ridgeSlope = (s, sigma) => (s / sigma) * Math.exp(-(s * s) / (2 * sigma * sigma));

/*
 * A D-ring: straight bar at the base, semicircular arch over it. Built as a
 * closed tube rather than a plain torus so it reads as a D and not a keyring.
 */
function makeDRingGeometry(outerRadius, tubeRadius) {
  const pts = [];
  const a = outerRadius;
  pts.push(new THREE.Vector3(-a * 0.92, tubeRadius * 0.6, 0));
  pts.push(new THREE.Vector3(a * 0.92, tubeRadius * 0.6, 0));
  for (let i = 1; i < 22; i++) {
    const t = (i / 22) * Math.PI;
    pts.push(new THREE.Vector3(a * Math.cos(t), a * Math.sin(t) + tubeRadius * 0.6, 0));
  }
  const curve = new THREE.CatmullRomCurve3(pts, true, 'catmullrom', 0.4);
  return new THREE.TubeGeometry(curve, 96, tubeRadius, 10, true);
}

/*
 * Seat an object on the sphere at (lat, lon) with local +Y pointing outward.
 *
 * The basis is built explicitly rather than with setFromUnitVectors, which
 * leaves the twist about the normal undefined — that made every ring tab face a
 * different arbitrary direction. Here local X follows the meridian and local Z
 * runs east, so a tab loop always stands along the meridian. Both tangents stay
 * well defined at the poles, where an up-cross-normal basis would collapse.
 */
/*
 * Rewrite a partial sphere's V so it samples the ball's panel map at its TRUE
 * latitude.
 *
 * Anything welded to the skin borrows `this.ball.material` so it inherits the
 * finish. That material carries the two-tone panel map, and SphereGeometry
 * normalises V across whatever theta RANGE it was given — so a collar cap
 * spanning eight degrees of latitude still gets V from 0 to 1 and samples the
 * whole map, pole to pole, compressed into the collar. On a single-colour ball
 * that is invisible. On a two-tone one it drags the second colour up around the
 * valve, which is exactly what it looked like.
 *
 * Only V is touched. U already spans 0..1 for a full ring, and recomputing it
 * from position would collapse the duplicated seam vertex that SphereGeometry
 * emits at u = 1, tearing the texture along one meridian.
 */
function applySkinLatitudeUV(geo) {
  const pos = geo.attributes.position;
  const uv = geo.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const r = Math.hypot(x, y, z) || 1;
    uv.setY(i, 1 - Math.acos(clamp(y / r, -1, 1)) / Math.PI);
  }
  uv.needsUpdate = true;
  return geo;
}

function placeOnSphere(obj, latRad, lonRad, radius) {
  const sLat = Math.sin(latRad);
  const cLat = Math.cos(latRad);
  const sLon = Math.sin(lonRad);
  const cLon = Math.cos(lonRad);
  const dir = new THREE.Vector3(cLat * sLon, sLat, cLat * cLon);
  const north = new THREE.Vector3(-sLat * sLon, cLat, -sLat * cLon);
  const east = new THREE.Vector3(cLon, 0, -sLon);
  obj.position.copy(dir).multiplyScalar(radius);
  obj.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(north, dir, east));
  return obj;
}

/*
 * Panel maps for two-tone balls: alternating gores in two colours.
 *
 * Two textures, both one texel per half-gore so every colour change lands
 * exactly on a seam:
 *   colour map — the tint per gore
 *   ORM map    — G = roughness, B = metalness, R = finish id
 *
 * Per-gore roughness and metalness are not optional. White is a pearlised film
 * at 0.35/0.28 while the metals are mirrors at 1.0/0.045, so a white-and-gold
 * ball needs both varying across the seam, not just the colour.
 *
 * The pole caps take the primary colour. They are single circular panels on the
 * real product, so striping them by longitude would be wrong.
 */
function buildPanelMaps(gores, specA, specB) {
  const W = gores * 2; // one texel per half gore -> edges land on seams
  const H = 256;
  const capLat = Math.PI / 2 - THREE.MathUtils.degToRad(CAP_ANGLE_DEG);
  const colour = new Uint8Array(W * H * 4);
  const orm = new Uint8Array(W * H * 4);
  const finishOf = (spec) => FINISH_ID[spec.finish || 'none'] ?? 0;

  for (let j = 0; j < H; j++) {
    const lat = Math.PI / 2 - ((j + 0.5) / H) * Math.PI;
    const inCap = Math.abs(lat) >= capLat;
    for (let i = 0; i < W; i++) {
      // Gore centres sit on u = k/N, so texel i belongs to gore round((i+.5)/2).
      const gore = Math.round((i + 0.5) / 2) % gores;
      const spec = inCap || gore % 2 === 0 ? specA : specB;
      const o = (j * W + i) * 4;
      colour[o] = (spec.color >> 16) & 255;
      colour[o + 1] = (spec.color >> 8) & 255;
      colour[o + 2] = spec.color & 255;
      colour[o + 3] = 255;
      orm[o] = finishOf(spec);
      orm[o + 1] = Math.round(clamp(spec.roughness, 0, 1) * 255);
      orm[o + 2] = Math.round(clamp(spec.metalness, 0, 1) * 255);
      orm[o + 3] = 255;
    }
  }

  const mk = (data, srgb) => {
    const t = new THREE.DataTexture(data, W, H, THREE.RGBAFormat);
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;
    // Nearest: panel edges must be hard and land on the seam, never blended.
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestFilter;
    t.generateMipmaps = false;
    t.needsUpdate = true;
    return t;
  };
  return { colour: mk(colour, true), orm: mk(orm, false) };
}

/*
 * Iridescent hue ramp as a 1D lookup, indexed by the Fresnel term. Keeping it
 * as data rather than GLSL means the ramp is tunable without touching a shader.
 *
 * The reference photograph dictates the direction: magenta and pink at grazing
 * angles round the rim, cyan and yellow face-on. So index 0 (grazing) is
 * magenta and index 1 (facing) is pale yellow.
 */
const IRID_RAMP = [
  [0.00, [1.0, 0.42, 0.94]], // rim — magenta
  [0.22, [0.93, 0.58, 1.0]], // pink / lavender
  [0.45, [0.62, 0.86, 1.0]], // pale blue
  [0.66, [0.55, 1.0, 0.92]], // cyan
  [0.84, [0.82, 1.0, 0.7]], // mint
  [1.0, [1.0, 0.97, 0.72]], // face-on — pale yellow
];

function buildIridescentLut() {
  const W = 128;
  const data = new Uint8Array(W * 4);
  for (let i = 0; i < W; i++) {
    const t = i / (W - 1);
    let a = IRID_RAMP[0];
    let b = IRID_RAMP[IRID_RAMP.length - 1];
    for (let k = 0; k < IRID_RAMP.length - 1; k++) {
      if (t >= IRID_RAMP[k][0] && t <= IRID_RAMP[k + 1][0]) {
        a = IRID_RAMP[k];
        b = IRID_RAMP[k + 1];
        break;
      }
    }
    const f = b[0] === a[0] ? 0 : (t - a[0]) / (b[0] - a[0]);
    for (let c = 0; c < 3; c++) {
      data[i * 4 + c] = Math.round((a[1][c] + (b[1][c] - a[1][c]) * f) * 255);
    }
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, W, 1, THREE.RGBAFormat);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/*
 * Flake field for the holographic finish — a jittered-grid mosaic, not white
 * noise. Two separate things forced this rewrite.
 *
 * The bug: the old texture stored raw uniform noise in R and the shader
 * thresholded it with step(flakeCut, r) AFTER the mip chain had already
 * averaged it. Averaging a uniform field pulls it toward 0.5, so the tail above
 * 0.895 largely disappears — measured at 1.3% of pixels firing at 600px and
 * 1.7% at 1600px, against the 10.5% the threshold nominally asks for. The
 * survivors were then added at full saturation x1.5 onto a near-white mirror,
 * where they clipped to white and lost their chroma, so the finish measured
 * identical to plain Silver (sat>0.10: 0.03% rendered vs 9.26% photographed).
 * This is the flakeScale trap reached from the other direction.
 *
 * The fix is to store COVERAGE, already thresholded, so a mip level averages
 * the mask instead of the noise beneath it. A half-covered texel then reads as
 * half a flake — correct antialiasing — rather than a vanished one.
 *
 * The structure: the reference photograph's saturation autocorrelation has a
 * clear negative lobe at ~10px on a 1176px ball, so flecks repel at a
 * characteristic pitch. That is cellular, not independent random texels — real
 * holographic film is embossed "crushed ice". Facets are cut from a jittered
 * Voronoi grid and clipped against the cell border, so they read as angular
 * shards rather than round dots.
 *
 *   R = coverage (1 inside a lit facet, 0 outside, soft at the edge)
 *   G = per-facet hue
 *   B = per-facet phase
 *
 * CELLS x uFlakeScale is the facet count around the equator: 64 x 3.0 = 192,
 * against ~195 measured off the photograph.
 */
const FLAKE_CELLS = 64;

function buildFlakeNoise(litFraction, facetRadius) {
  const W = 512;
  const CELL = W / FLAKE_CELLS;
  const data = new Uint8Array(W * W * 4);

  // Deterministic hash rather than Math.random, so the flake field is stable
  // across reloads and across the live view and the export render.
  const hash2 = (ix, iy, salt) => {
    let h = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(salt, 1442695041)) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };

  const rad = CELL * facetRadius;

  for (let y = 0; y < W; y++) {
    const gy0 = Math.floor(y / CELL);
    for (let x = 0; x < W; x++) {
      const gx0 = Math.floor(x / CELL);
      let best = 1e9;
      let second = 1e9;
      let bk = -1;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          // Hash the WRAPPED cell index but position the centre in unwrapped
          // space, so the texture tiles seamlessly under RepeatWrapping.
          const wgx = (((gx0 + dx) % FLAKE_CELLS) + FLAKE_CELLS) % FLAKE_CELLS;
          const wgy = (((gy0 + dy) % FLAKE_CELLS) + FLAKE_CELLS) % FLAKE_CELLS;
          const px = (gx0 + dx + 0.15 + 0.7 * hash2(wgx, wgy, 1)) * CELL;
          const py = (gy0 + dy + 0.15 + 0.7 * hash2(wgx, wgy, 2)) * CELL;
          const d = Math.hypot(x + 0.5 - px, y + 0.5 - py);
          if (d < best) {
            second = best;
            best = d;
            bk = wgy * FLAKE_CELLS + wgx;
          } else if (d < second) {
            second = d;
          }
        }
      }
      const gx = bk % FLAKE_CELLS;
      const gy = (bk - gx) / FLAKE_CELLS;
      const isLit = hash2(gx, gy, 3) < litFraction;

      // Clip the facet against both its own radius and the cell border, so the
      // shard is angular where neighbours crowd it and rounded where they do not.
      const border = 0.5 * (second - best);
      const edge = Math.min(rad - best, border);
      const cov = isLit ? clamp(edge / 1.2, 0, 1) : 0;

      const o = (y * W + x) * 4;
      data[o] = Math.round(cov * 255);
      data[o + 1] = Math.round(hash2(gx, gy, 4) * 255);
      data[o + 2] = Math.round(hash2(gx, gy, 5) * 255);
      data[o + 3] = 255;
    }
  }

  const tex = new THREE.DataTexture(data, W, W, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/*
 * Momentary highlight on the hardware when the fixings view is opened. The
 * valve and fittings are small and mirror-adjacent, so they can be genuinely
 * hard to spot; a brief rim glow says "look here" and then gets out of the way.
 *
 * Done as a Fresnel rim on the hardware materials rather than an outline pass —
 * an outline pass would mean vendoring EffectComposer and OutlinePass, which
 * the no-build-step constraint rules out.
 */
const HIGHLIGHT = {
  colour: [0.16, 1.0, 0.38],
  holdMs: 2000,
  fadeMs: 700,
  /*
   * Stroke width in PIXELS, not world units, so the outline reads the same
   * whether the camera is at the default framing or pushed right up against the
   * valve in the fixings view.
   */
  strokePx: 3.0,
};


/* Tunables for the two shader finishes. */
const FINISH = {
  /*
   * How hard the thin-film hue rides over the base tint. Swept against the
   * photographed per-Fresnel-band saturations (RMS across the eight bands):
   * 1.35 -> 0.0410, 1.15 -> 0.0366, 1.00 -> 0.0530, 0.85 -> 0.0711. 1.15 is the
   * minimum; the previous 1.35 ran the mid bands about a third hot.
   */
  iridStrength: 1.15,
  iridMilk: 0.24, // wash toward white — milky, but the studio must stay legible
  /*
   * The real film is slightly see-through — Zac, against the product. Applied
   * as per-fragment alpha rather than MeshPhysicalMaterial.transmission,
   * because the finish is chosen per fragment from the ORM map: a global
   * transmission would make the silver gores of a two-tone Iridescent/Silver
   * ball transparent too. Only the iridescent gores take it.
   */
  iridAlpha: 0.86,
  /*
   * UV multiplier on a 256px noise texture, so effective flake count across the
   * sphere is this x 256. Set it too high and every mip averages the noise to
   * flat grey, the threshold never fires and no flecks appear at all. Tuned so
   * a fleck lands near pixel-sized at the default framing.
   */
  flakeScaleAt100cm: 3.0,
  /*
   * Fraction of facets that are lit, and the facet radius as a fraction of the
   * cell. Coverage is baked into the texture now, so these are generation-time
   * parameters, not a shader threshold — see buildFlakeNoise for why that
   * distinction is the whole bug fix. Tuned against the photographed density:
   * 9.26% of pixels above saturation 0.10.
   */
  flakeLit: 0.34,
  flakeRadius: 0.38, // -> 9.19% texture coverage, against 9.26% photographed
  /*
   * Fleck amplitude. Measured, not guessed: the photograph caps fleck
   * saturation at p99 = 0.176 with essentially nothing above 0.30. Swept
   * against that: 0.5 -> f15 0.15%, 1.0 -> 0.73%, 1.8 -> 1.37%, 3.0 -> 1.90%,
   * 5.0 -> 2.10%, against a photographed 2.15%. The curve is asymptotic because
   * ACES desaturates highlights this bright, so past ~3 the extra amplitude
   * buys nothing and only risks clipping flecks to white. 3.0 lands f15 1.90%,
   * f20 0.42% and p99 0.169 against 2.15% / 0.53% / 0.176 photographed.
   */
  sparkle: 3.0,
  /* How far the fleck hue rotates from grazing to face-on. Diffraction, not
   * glitter: a holographic fleck changes colour as the ball turns, and without
   * this the field reads as static confetti. */
  flakeShift: 0.55,
  /*
   * Specular level below which flecks stay dark. Measured, not guessed: at 0.22
   * the flecks vanished entirely, because totalSpecular on this environment
   * sits well below that almost everywhere. Low threshold, wide ramp — the gate
   * still darkens flecks in shadowed areas without extinguishing the effect.
   */
  sparkleGate: 0.05,
};

const seamMapCache = new Map();

function buildSeamNormalMap(gores) {
  if (seamMapCache.has(gores)) return seamMapCache.get(gores);

  const W = SEAM.tileWidth;
  const H = SEAM.tileHeight;
  const goreSpan = (Math.PI * 2) / gores; // radians of longitude per tile
  const latPerPx = Math.PI / H;
  const capLat = Math.PI / 2 - THREE.MathUtils.degToRad(CAP_ANGLE_DEG);
  const data = new Uint8Array(W * H * 4);

  for (let j = 0; j < H; j++) {
    // Row 0 is the north pole: SphereGeometry puts v = 0 at +Y.
    const lat = Math.PI / 2 - (j + 0.5) * latPerPx;
    const cosLat = Math.max(Math.cos(lat), 1e-4);
    const inBody = Math.abs(lat) < capLat;

    // Welded ring at the cap boundary. Sign flips with hemisphere because +v
    // points south, so the slope relative to the bitangent inverts.
    const ny =
      -SEAM.ringAmplitude * ridgeSlope(Math.abs(lat) - capLat, SEAM.ringSigmaRad) * (lat >= 0 ? 1 : -1);

    for (let i = 0; i < W; i++) {
      /*
       * Arc distance from the seam, not longitude difference. A meridian's
       * perpendicular separation shrinks by cos(latitude), so measuring in arc
       * keeps the ridge a constant physical width from equator to cap instead
       * of tapering to nothing.
       */
      const dLon = ((i + 0.5) / W - 0.5) * goreSpan;
      let nx = inBody ? -SEAM.amplitude * ridgeSlope(dLon * cosLat, SEAM.sigmaRad) : 0;
      let nyTotal = ny;


      const len = Math.hypot(nx, nyTotal, 1);
      const o = (j * W + i) * 4;
      data[o] = Math.round(((nx / len) * 0.5 + 0.5) * 255);
      data[o + 1] = Math.round(((nyTotal / len) * 0.5 + 0.5) * 255);
      data[o + 2] = Math.round((1 / len) * 0.5 * 255 + 127.5);
      data[o + 3] = 255;
    }
  }

  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.repeat.set(gores, 1);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  seamMapCache.set(gores, tex);
  return tex;
}

const VIEWS = {
  /*
   * targetY sits slightly BELOW the ball centre so there is room under the ball
   * for the floor and its shadow, without the ball reading as off-centre.
   *
   * The margin arithmetic is unforgiving and worth keeping written down. At
   * DIST_DEFAULT 4.6 and CAM_FOV 32 the half-height at the ball's distance is
   * 1.319 world units, so a radius-1 ball spans 75.8% of the frame and leaves
   * only 12.1% margin top and bottom. Every 0.1 of targetY moves the ball 3.8%
   * of the frame height. -0.3 therefore left a 0.7% top margin — the ball
   * visibly jammed against the top edge, which is what it looked like.
   * -0.08 gives 9.1% above and 15.1% below: grounded, still centred.
   */
  default: { az: 0, elev: 6, targetY: -0.08 },
  /*
   * The default view swung 180 degrees. Deliberately identical in every other
   * respect: the back print has to be judged on the same framing as the front,
   * or the customer reads a difference in the camera as a difference in the
   * print.
   */
  back: { az: 180, elev: 6, targetY: -0.08 },
  // Three-quarter-top on the valve pole. Steeper than the free-orbit clamp
  // allows, which is fine: this is a fixed preset, and it is what generates the
  // spec sheet's fixing diagram.
  fixings: { az: 34, elev: 52 },
  /*
   * The drawing's own framing. Shares the fixings preset's angles so the
   * hardware still reads, but keeps the ball CENTRED and whole in the frame —
   * the fixings preset deliberately pushes the ball off-centre and crops in on
   * the cluster, which is right for a close-up and wrong for a technical
   * elevation that has to show the whole object.
   */
  technical: { az: 30, elev: 72 },
};

/*
 * Environment tuning. These are the knobs for the look-and-adjust pass, kept
 * together so a change hot-reloads through `shopify theme dev`.
 *
 * The supplied plate is an 8-bit JPEG, so the softboxes and skylights are
 * clipped flat at pure white and carry no specular headroom. Against a
 * roughness-0.045 chrome ball that reads dull — real studio sources are orders
 * of magnitude brighter than the walls they light. We reconstruct an
 * approximate high range by expanding the top of the luminance curve before
 * PMREM sees it. This is an informed fake, not measured data: it restores the
 * ratio between source and wall, not their absolute values.
 */
const ENV = {
  rotationDeg: 0, // yaw, to line the studio up with the reference framing
  intensity: 1.0,
  /*
   * Threshold must sit above the walls, not just above mid grey. This plate is
   * a white studio: its median linear luminance is 0.74 and 13% of it exceeds
   * 0.80, so a 0.80 threshold expands the walls and floor along with the lights
   * and the ball washes out. The actual sources — softboxes and skylights — are
   * the top ~3.5% (p98 = 0.976), so the threshold belongs just under that.
   */
  highlightThreshold: 0.96,
  highlightPeak: 14, // gain applied to a fully clipped white pixel
  highlightCurve: 2.0, // higher = expansion confined closer to pure white
};

/*
 * Material table, keyed on the variant option value. Matched case-insensitively
 * with a fallback so a capitalisation tidy-up in the admin cannot silently
 * break the renderer — the live values are already inconsistent ("Rose gold"
 * has a lowercase g while the rest are title case).
 *
 * The tints are MEASURED off the product photography, not invented. For a metal
 * the R:G:B ratio is preserved across brightness, so the ball's chromaticity is
 * its tint: median chromaticity over mid-brightness pixels gives the ratio, and
 * the film's own peak reflectance comes from comparing measured luminance
 * against what that ratio alone predicts. Note the ratio must NOT simply be
 * scaled by how dark the ball looks — a red mirror ball is dark *because* it
 * reflects one channel, so doing that applies the same darkening twice and
 * every saturated colour comes out muddy.
 *
 * `finish` marks the two that need more than a tint (phase 7 shaders).
 */
/*
 * These balls are mirror-finish PVC — a metallised film on a flexible polymer,
 * not machined metal. The metal layer is real, so metalness stays at 1, but the
 * surface is not optically flat: roughness is lifted well above a true mirror,
 * and the film undulates over each inflated panel (see RIPPLE).
 */
const METAL = { metalness: 1.0, roughness: 0.07 };

/*
 * Surface undulation of the film. Baked into the seam tile, which repeats once
 * per gore — and that repetition is physically right rather than a shortcut:
 * every gore is a separately welded panel that bulges the same way between its
 * seams. Horizontal harmonics are whole numbers so the pattern wraps seamlessly
 * across the tile edge.
 */
/*
 * Film undulation. Computed in the shader from the 3D surface position, NOT
 * baked into the equirectangular seam map.
 *
 * Baking it there cannot work: phase would be a function of longitude, but arc
 * distance along a circle of latitude is R*cos(lat)*dlon, so the waves compress
 * as cos(lat) falls — 2x tighter by the 60-degree cap boundary, 11x by 85 — and
 * converge into a starburst at the pole. Sampling 3D noise on the unit sphere
 * is isotropic everywhere and has no preferred axis, so there is nothing to
 * converge. It also frees the ripple from the seam tile's per-gore repeat.
 */
const RIPPLE = {
  amplitude: 0.008, // tangential gradient scale — a sheen, not a texture
  frequency: 210.0, // cycles across the sphere, roughly
};

/*
 * Quality tiers — the mobile pass.
 *
 * Measured before any of this was written, which changed what it does. The
 * assumption in the performance notes was that fragment cost dominates and the
 * first lever is the ripple's second octave. Half true: the ripple IS about 45%
 * of frame cost at the mobile backing size (2.21ms -> 1.21ms with it off on an
 * M-series GPU). But going from a 686px to a 1400px backing — 4.2x the pixels —
 * only moved the frame from 2.21ms to 3.01ms, so the frame is nowhere near
 * fill-bound. It is bound by geometry and draw calls.
 *
 * And the real hazard was not frame time at all. It was memory. A single logo
 * allocated a 4096x2048 decal three times over: a 32MB canvas, a 32MB ImageData
 * working buffer, and a ~43MB GPU texture with mips. ~107MB for one upload, on
 * top of the environment and everything else. iOS Safari discards tabs well
 * below that, so on a phone the configurator would not have run slowly, it would
 * have reloaded itself the moment a customer added artwork.
 *
 * So the mobile tier cuts memory and geometry first, and fragment work second.
 */
const QUALITY = {
  desktop: {
    decalWidth: 4096,
    ballSegments: [192, 128],
    rippleOctaves: 2,
    antialias: true,
    exportSize: 1600,
  },
  mobile: {
    // 2048 gives the logo ~260px of equirect, which was the pre-27-Aug value
    // and acceptable at a phone's backing size; it drops the decal's total
    // footprint from ~107MB to ~27MB.
    decalWidth: 2048,
    /*
     * 96x64 is 12k triangles against 49k, per sphere, and there are two of them
     * — the ball and the decal shell. The silhouette is the only thing that
     * suffers and at a 343px CSS canvas the difference is not resolvable.
     */
    ballSegments: [96, 64],
    rippleOctaves: 1,
    /*
     * No MSAA. At devicePixelRatio 2 the backing store is already supersampled
     * 2x, so MSAA buys very little and costs bandwidth, which is exactly what
     * mobile GPUs are short of.
     */
    antialias: false,
    exportSize: 1200,
  },
};

/*
 * Coarse pointer AND a small viewport, rather than a user-agent test. A UA
 * string tells you what the browser wants to be called; these two together tell
 * you that you are drawing into a small canvas with a finger on it, which is
 * what actually decides the budget. deviceMemory is consulted where offered
 * (Chrome only) as a third opinion for weak Android hardware.
 */
function pickQuality() {
  if (typeof window === 'undefined' || !window.matchMedia) return QUALITY.desktop;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const small = window.matchMedia('(max-width: 900px)').matches;
  const weak = typeof navigator !== 'undefined' && navigator.deviceMemory && navigator.deviceMemory <= 4;
  return (coarse && small) || weak ? QUALITY.mobile : QUALITY.desktop;
}

const Q = pickQuality();

const MATERIALS = {
  silver: { color: 0xfaf9fe, ...METAL },
  gold: { color: 0xffe9ae, ...METAL },
  'rose gold': { color: 0xffcbb3, ...METAL },
  pink: { color: 0xffa6ca, ...METAL },
  purple: { color: 0xffaafc, ...METAL },
  blue: { color: 0x579efd, ...METAL },
  'light pink': { color: 0xdb86b5, ...METAL },
  'grey violet': { color: 0xb387ce, ...METAL },
  'sky blue': { color: 0xc2edf7, ...METAL },
  green: { color: 0x69e881, ...METAL },
  orange: { color: 0xff8547, ...METAL },
  black: { color: 0x313131, ...METAL },
  cyan: { color: 0xb7efd6, ...METAL },
  red: { color: 0xf55a5a, ...METAL },
  'grass green': { color: 0xbde969, ...METAL },
  'navy blue': { color: 0x5093dd, ...METAL },

  // White is the exception: a pearlised film, not a mirror.
  white: { color: 0xfbf9ff, metalness: 0.35, roughness: 0.28 },

  // Finishes. Tint is the base the shader works from.
  // Milky, not chromed: metalness well below 1 lifts a diffuse term, and the
  // higher roughness washes the studio reflection out.
  iridescent: { color: 0xf3e3ff, metalness: 0.72, roughness: 0.13, finish: 'iridescent' },
  'silver holographic': { color: 0xd6d5dc, ...METAL, finish: 'holographic' },
};
const MATERIAL_FALLBACK = MATERIALS.silver;

function materialSpecFor(name) {
  const key = String(name || '').trim().toLowerCase();
  return MATERIALS[key] || MATERIAL_FALLBACK;
}

/* Finish ids, encoded into the panel map's red channel for the shaders. */
const FINISH_ID = { none: 0, iridescent: 128, holographic: 255 };

/*
 * Hardware. Dimensions are in real centimetres and converted to world units
 * against the selected diameter, so the valve and fixings are physically
 * constant and appear relatively larger on a 40cm ball than on a 300cm one.
 */
const FIX = {
  /* --- MEASURED off a real 80cm ball by Zac, 29 July 2026 --- */
  valveDiameterCm: 6.0,
  valveHoleDiameterCm: 3.0, // open centre
  valveHeightCm: 0.8, // it barely stands proud — 8mm
  valveCollarWidthCm: 3.0, // welded material ring around the valve
  exhaustHoleDiameterCm: 1.0,
  exhaustHoleGapCm: 1.5, // clear gap from the valve edge to the hole
  exhaustHoleCount: 2,
  mountRadiusCm: 15.0, // measured on the 80cm ball, and the floor for all sizes
  mountSpreadFromCm: 100, // above this the fixings start spreading outward
  mountSpreadMaxDeg: 26, // angle from the pole at 300cm — stays inside the cap

  /* --- still inferred, NOT measured --- */
  tabLoopDiameterCm: 2.2, // small moulded eye, read off the close-up
  tabPedestalHeightCm: 0.35,
  tabDiscDiameterCm: 5.5, // ~5.4cm measured against the photo's 15cm scale
  dRingDiameterCm: 4.8,
  dRingDiscDiameterCm: 6.4,
  tubeDiameterCm: 0.7,
  weldHeightCm: 0.22,
};

/*
 * Mount distance is in centimetres, never a fixed angle. A fixed angle breaks
 * at small sizes: the mounts drift inside the valve collar and the tabs vanish
 * underneath it.
 */

/*
 * How far a flat part of footprint radius r floats above the sphere at its rim.
 * Rigid parts are seated by this much so their edge meets the skin instead of
 * hovering over it — which is also how a real moulded fitting sits in its
 * welded recess.
 */
const sagittaFor = (r) => BALL_RADIUS - Math.sqrt(Math.max(0, BALL_RADIUS * BALL_RADIUS - r * r));

/*
 * Distance of each fixing from its pole.
 *
 * Held at the measured 15cm up to 100cm. Above that the fixings spread
 * progressively outward, because a big ball hung from four points needs a wider
 * base than a scaled-up small one — a fixed 15cm puts all four within 5.7
 * degrees of the pole at 300cm, which is structurally a single point and gives
 * the rigging no lateral leverage.
 *
 * Interpolated as an ANGLE rather than a distance, since the angle is what sets
 * the rigging geometry, then converted back to centimetres for the builder.
 * Capped below the 30-degree pole cap boundary so the mounts stay on the cap
 * panel rather than straddling its weld.
 *
 * [UNCONFIRMED] The 26-degree figure is a judgement call, not a factory spec.
 */
function mountRadiusCmFor(sizeCm) {
  const base = FIX.mountRadiusCm;
  if (sizeCm <= FIX.mountSpreadFromCm) return base;
  const baseAngle = base / (FIX.mountSpreadFromCm / 2); // radians at the threshold
  const maxAngle = THREE.MathUtils.degToRad(FIX.mountSpreadMaxDeg);
  const t = clamp((sizeCm - FIX.mountSpreadFromCm) / (300 - FIX.mountSpreadFromCm), 0, 1);
  const angle = baseAngle + (maxAngle - baseAngle) * t;
  return clampMountInsideCap(angle, sizeCm) * (sizeCm / 2);
}

/*
 * Keep the whole welded disc on the cap panel.
 *
 * The 15cm measured on the 80cm ball sits comfortably inside its cap, but the
 * same 15cm on a 40cm ball is 43 degrees from the pole — well past the
 * 30-degree cap boundary, so the disc would be bonded across a gore seam. That
 * is poor practice on a real ball and looks wrong on a rendered one.
 *
 * [UNCONFIRMED] This only binds at 40cm and 60cm. It assumes the cap subtends a
 * constant angle at every size; if small balls actually have proportionally
 * larger caps, the measured 15cm may be right there and this clamp is wrong.
 * One real ball cannot tell us which.
 */
function clampMountInsideCap(angleRad, sizeCm) {
  const discAngle = (FIX.tabDiscDiameterCm / sizeCm); // radians, = (d/2)*2/size
  const margin = THREE.MathUtils.degToRad(1.5);
  const limit = THREE.MathUtils.degToRad(CAP_ANGLE_DEG) - discAngle - margin;
  return Math.min(angleRad, Math.max(limit, 0.05));
}

/* Counts are derived from size, never chosen (brief §7). */
const ringCountFor = (cm) => (cm <= 120 ? 2 : 4);
const dRingCountFor = (cm) => (cm <= 120 ? 1 : 2);

/*
 * Artwork decal. Painted into an equirectangular canvas and applied to a
 * slightly larger sphere: 1.0022 clears z-fighting without opening a visible
 * gap. UV print is matte ink over the metallic film, so the layer is rough and
 * barely reflective rather than sharing the ball's mirror finish.
 */
/*
 * Longitude the front print sits on. The default camera faces u = 0.25, so this
 * is where artwork goes — and it is shared with the fixing placement, which has
 * to keep clear of it. Two independent copies of this angle is exactly how a
 * hook ended up sitting directly over the logo.
 */
const ART_LON = Math.PI / 2;

/*
 * The same direction expressed in placeOnSphere's convention, which is NOT the
 * decal's.
 *
 * placeOnSphere builds dir = (cosLat*sin(lon), sinLat, cosLat*cos(lon)), so its
 * lon = 0 points at +Z. SphereGeometry's u = 0.25 also lands on +Z, and the
 * decal writes the front print at ART_LON = PI/2 which IS u = 0.25. The two
 * spaces are therefore offset by exactly PI/2, and hardware placed at "ART_LON"
 * in mount coordinates comes out ninety degrees away from the artwork.
 *
 * That is not hypothetical: it put a ring dead over the logo on every two-hook
 * ball, and it survived a review precisely because the arithmetic was checked
 * on its own instead of against the geometry. Measure the world azimuths.
 */
const ART_LON_MOUNT = ART_LON - Math.PI / 2;

const DECAL = {
  /*
   * 4096 wide, not 2048. Artwork is capped at 50% of diameter, which is about
   * 46 degrees of longitude, so at 2048 the logo only ever occupied ~260px of
   * equirect regardless of how good the source was — then got magnified back up
   * on screen. That ceiling, not the upload, was most of the reported softness
   * on vector logos. 4096 gives it ~520px, comfortably above its on-screen size
   * at the export resolution.
   *
   * Costs ~32MB for the canvas and as much again for the texture. First lever
   * to pull in the mobile pass, alongside the ripple octaves.
   */
  width: Q.decalWidth,
  height: Q.decalWidth / 2,
  radiusScale: 1.0022,
  inkRoughness: 0.62,
  inkEnvIntensity: 0.25,
};

/*
 * Slider ceiling, as a percentage of diameter.
 *
 * [UNCONFIRMED — awaiting Amy] The cap-derived geometric limit is ~105%, which
 * is not a usable maximum: with artwork locked to the equator the pole cap is
 * simply not what constrains it. 50% matches the ceiling the 2D build shipped.
 */
const LOGO_MAX_PCT_PROVISIONAL = 50;

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

const srgbToLinear = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

/*
 * Load an equirectangular JPEG and expand its clipped highlights into a float
 * texture. Falls back to the plain sRGB texture if the pixels cannot be read,
 * which keeps the ball correct-but-flat rather than broken.
 */
async function loadEquirect(url) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = () => rej(new Error(`environment map failed to load: ${url}`));
    img.src = url;
  });

  const w = img.naturalWidth;
  const h = img.naturalHeight;
  let px = null;
  try {
    const cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    px = ctx.getImageData(0, 0, w, h).data;
  } catch (err) {
    px = null; // cross-origin without CORS — canvas is tainted
  }

  if (!px) {
    const tex = new THREE.Texture(img);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return { texture: tex, expanded: false };
  }

  const { highlightThreshold: t, highlightPeak: peak, highlightCurve: p } = ENV;
  const out = new Float32Array(w * h * 4);
  /*
   * Rows are flipped on the way in. ImageData row 0 is the top of the picture,
   * but a DataTexture defaults to flipY = false where an image-backed texture
   * defaults to true — so copying straight across lands the studio ceiling
   * under the ball. Doing it here rather than via texture.flipY keeps the
   * orientation explicit and independent of upload-path semantics.
   */
  for (let y = 0; y < h; y++) {
    const src = (h - 1 - y) * w * 4;
    const dst = y * w * 4;
    for (let x = 0; x < w; x++) {
      const s = src + x * 4;
      const o = dst + x * 4;
      const r = srgbToLinear(px[s] / 255);
      const g = srgbToLinear(px[s + 1] / 255);
      const b = srgbToLinear(px[s + 2] / 255);
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      let gain = 1;
      if (luma > t) gain = 1 + (peak - 1) * Math.pow((luma - t) / (1 - t), p);
      out[o] = r * gain;
      out[o + 1] = g * gain;
      out[o + 2] = b * gain;
      out[o + 3] = 1;
    }
  }

  const tex = new THREE.DataTexture(out, w, h, THREE.RGBAFormat, THREE.FloatType);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.colorSpace = THREE.LinearSRGBColorSpace; // already linearised above
  tex.needsUpdate = true;
  return { texture: tex, expanded: true };
}

/* ------------------------------------------------- damped orbit controller */

/*
 * Rotate and zoom only — no pan, no free camera. Written here rather than
 * vendored from three/examples/jsm so we do not carry a second file (brief §3).
 */
class DampedOrbit {
  constructor(canvas) {
    this.canvas = canvas;
    this.az = THREE.MathUtils.degToRad(VIEWS.default.az);
    this.elev = THREE.MathUtils.degToRad(VIEWS.default.elev);
    this.dist = DIST_DEFAULT;
    this.targetDist = DIST_DEFAULT;
    this.minDist = DIST_MIN_SMALL;

    this.azVel = 0;
    this.elevVel = 0;
    this.damping = 0.92;
    this.tween = null; // { az, elev } while animating to a preset

    /*
     * Look-at point. Always the origin for free orbit — there is no panning.
     * Presets may shift it along the polar axis so the hardware sits near the
     * middle of the frame instead of being cropped at the silhouette edge.
     */
    this.target = new THREE.Vector3(0, VIEWS.default.targetY, 0);
    this.targetGoal = new THREE.Vector3(0, VIEWS.default.targetY, 0);

    /*
     * Optional orbit limits, set by a preset. Null means free orbit under the
     * global clamps. A preset that sits outside those clamps must supply its
     * own, or the customer's first drag snaps the camera tens of degrees in one
     * frame — the fixings view sits at 52 degrees against a 45-degree ceiling.
     */
    this.limits = null;

    this._pointers = new Map();
    this._pinchStart = 0;
    this._last = null;
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onWheel = this._onWheel.bind(this);

    canvas.addEventListener('pointerdown', this._onDown);
    canvas.addEventListener('pointermove', this._onMove);
    canvas.addEventListener('pointerup', this._onUp);
    canvas.addEventListener('pointercancel', this._onUp);
    canvas.addEventListener('wheel', this._onWheel, { passive: false });
  }

  /* Active elevation bounds: a preset's own window, or the global clamps. */
  _elevBounds() {
    const l = this.limits;
    return l
      ? [l.elevCentre - l.elevRange, l.elevCentre + l.elevRange]
      : [THREE.MathUtils.degToRad(ELEV_MIN), THREE.MathUtils.degToRad(ELEV_MAX)];
  }

  get dragging() {
    return this._pointers.size > 0;
  }

  _onDown(e) {
    this.canvas.setPointerCapture(e.pointerId);
    this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.canvas.classList.add('is-dragging');
    this.tween = null; // any touch cancels a preset animation
    this.azVel = 0;
    this.elevVel = 0;
    if (this._pointers.size === 2) this._pinchStart = this._pinchDist() / this.dist;
  }

  _onMove(e) {
    const p = this._pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;

    if (this._pointers.size === 2) {
      const d = this._pinchDist();
      if (this._pinchStart > 0) this.targetDist = clamp(d / this._pinchStart, this.minDist, DIST_MAX);
      return;
    }

    // Full canvas width is roughly half a turn — matches the feel of dragging
    // the object itself rather than a slider.
    const rect = this.canvas.getBoundingClientRect();
    const kAz = Math.PI / Math.max(1, rect.width);
    const kEl = Math.PI / Math.max(1, rect.height);
    /*
     * az and elev are RAW here — deliberately unclamped. applyTo() runs them
     * through rubberBand() so the camera resists past a limit instead of
     * stopping dead, and update() springs them back once the pointer lifts.
     * Raw is bounded only generously, so a long drag cannot bank up an
     * overshoot that takes seconds to unwind.
     */
    const [eLo, eHi] = this._elevBounds();
    const eMax = THREE.MathUtils.degToRad(RUBBER.elevOvershootDeg) * RUBBER.rawOvershootFactor;
    this.az -= dx * kAz;
    this.elev += dy * kEl;
    this.elev = clamp(this.elev, eLo - eMax, eHi + eMax);
    if (this.limits) {
      const aLo = this.limits.azCentre - this.limits.azRange;
      const aHi = this.limits.azCentre + this.limits.azRange;
      const aMax = THREE.MathUtils.degToRad(RUBBER.azOvershootDeg) * RUBBER.rawOvershootFactor;
      this.az = clamp(this.az, aLo - aMax, aHi + aMax);
    }
    this.azVel = -dx * kAz;
    this.elevVel = dy * kEl;
  }

  _onUp(e) {
    this._pointers.delete(e.pointerId);
    if (this._pointers.size === 0) this.canvas.classList.remove('is-dragging');
  }

  _onWheel(e) {
    e.preventDefault();
    const step = Math.exp(clamp(e.deltaY, -120, 120) * 0.0016);
    const lo = this.limits ? this.limits.distMin : this.minDist;
    const hi = this.limits ? this.limits.distMax : DIST_MAX;
    this.targetDist = clamp(this.targetDist * step, lo, hi);
  }

  _pinchDist() {
    const [a, b] = [...this._pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  setSizeLimits(cm) {
    const t = clamp((cm - 40) / (300 - 40), 0, 1);
    this.minDist = DIST_MIN_SMALL + (DIST_MIN_LARGE - DIST_MIN_SMALL) * t;
    this.targetDist = clamp(this.targetDist, this.minDist, DIST_MAX);
  }

  goTo(preset, over = {}) {
    const v = VIEWS[preset] || VIEWS.default;
    const elev = over.elev == null ? v.elev : over.elev;
    this.tween = { az: THREE.MathUtils.degToRad(v.az), elev: THREE.MathUtils.degToRad(elev) };
    this.azVel = 0;
    this.elevVel = 0;
    /*
     * An explicit preset distance bypasses the zoom clamp, exactly as an
     * explicit elevation bypasses the tilt clamp. Presets are fixed framings
     * chosen to be legible; the clamps exist to stop free orbit finding a bad
     * angle, and they reassert themselves as soon as the customer interacts.
     */
    this.targetDist = over.dist == null ? clamp(DIST_DEFAULT, this.minDist, DIST_MAX) : over.dist;
    this.targetGoal.set(0, over.targetY || 0, 0);
    this.limits = over.limits || null;
  }

  /* Advance one frame. Returns true while anything is still moving. */
  update() {
    let moving = false;

    if (this.tween) {
      // Shortest way round, so a preset never takes the long way.
      let d = ((this.tween.az - this.az + Math.PI) % (Math.PI * 2)) - Math.PI;
      if (d < -Math.PI) d += Math.PI * 2;
      this.az += d * 0.12;
      this.elev += (this.tween.elev - this.elev) * 0.12;
      if (Math.abs(d) < 1e-3 && Math.abs(this.tween.elev - this.elev) < 1e-3) this.tween = null;
      moving = true;
    } else if (!this.dragging) {
      const [eLo, eHi] = this._elevBounds();
      const eMax = THREE.MathUtils.degToRad(RUBBER.elevOvershootDeg) * RUBBER.rawOvershootFactor;
      const aLo = this.limits ? this.limits.azCentre - this.limits.azRange : -Infinity;
      const aHi = this.limits ? this.limits.azCentre + this.limits.azRange : Infinity;
      const aMax = THREE.MathUtils.degToRad(RUBBER.azOvershootDeg) * RUBBER.rawOvershootFactor;

      if (Math.abs(this.azVel) > 1e-5 || Math.abs(this.elevVel) > 1e-5) {
        this.az = clamp(this.az + this.azVel, aLo - aMax, aHi + aMax);
        this.elev = clamp(this.elev + this.elevVel, eLo - eMax, eHi + eMax);
        this.azVel *= this.damping;
        this.elevVel *= this.damping;
        moving = true;
      } else {
        this.azVel = 0;
        this.elevVel = 0;
      }

      /*
       * Spring whatever is past a limit back onto it. This runs after the
       * inertia step and zeroes the velocity on that axis, or a fast flick
       * would keep feeding the overshoot faster than the spring drains it and
       * the view would sit pinned against the edge.
       */
      const eTarget = clamp(this.elev, eLo, eHi);
      if (Math.abs(eTarget - this.elev) > 1e-5) {
        this.elev += (eTarget - this.elev) * RUBBER.springBack;
        this.elevVel = 0;
        moving = true;
      }
      if (this.limits) {
        const aTarget = clamp(this.az, aLo, aHi);
        if (Math.abs(aTarget - this.az) > 1e-5) {
          this.az += (aTarget - this.az) * RUBBER.springBack;
          this.azVel = 0;
          moving = true;
        }
      }
    } else {
      moving = true;
    }

    const dd = this.targetDist - this.dist;
    if (Math.abs(dd) > 1e-4) {
      this.dist += dd * 0.16;
      moving = true;
    }
    if (this.target.distanceToSquared(this.targetGoal) > 1e-8) {
      this.target.lerp(this.targetGoal, 0.16);
      moving = true;
    }
    return moving;
  }

  /*
   * Finish every pending ease this instant. update() only advances inside the
   * render loop, so anything that captures a frame without waiting for the
   * loop — a spec-sheet diagram, an export taken straight after setView — would
   * otherwise photograph the camera mid-tween. Worse, requestAnimationFrame is
   * throttled or stopped outright in a background tab, so the tween may never
   * advance at all and the capture silently comes back on the previous view.
   */
  settle() {
    if (this.tween) {
      this.az = this.tween.az;
      this.elev = this.tween.elev;
      this.tween = null;
    }
    this.azVel = 0;
    this.elevVel = 0;
    // Land inside the limits, or a capture would photograph the elastic edge.
    const [eLo, eHi] = this._elevBounds();
    this.elev = clamp(this.elev, eLo, eHi);
    if (this.limits) {
      this.az = clamp(this.az, this.limits.azCentre - this.limits.azRange, this.limits.azCentre + this.limits.azRange);
    }
    this.dist = this.targetDist;
    this.target.copy(this.targetGoal);
  }

  /*
   * The only place the raw orbit angles become camera angles, and therefore the
   * one place the elastic edge is applied. Inside the limits rubberBand() is
   * the identity, so nothing changes for the 99% of dragging that never reaches
   * an edge.
   */
  _resisted() {
    const [eLo, eHi] = this._elevBounds();
    const elev = rubberBand(this.elev, eLo, eHi, THREE.MathUtils.degToRad(RUBBER.elevOvershootDeg));
    let az = this.az;
    if (this.limits) {
      az = rubberBand(
        this.az,
        this.limits.azCentre - this.limits.azRange,
        this.limits.azCentre + this.limits.azRange,
        THREE.MathUtils.degToRad(RUBBER.azOvershootDeg)
      );
    }
    return { az, elev };
  }

  applyTo(camera) {
    const { az, elev } = this._resisted();
    const d = this.dist;
    const ce = Math.cos(elev);
    camera.position.set(
      this.target.x + d * ce * Math.sin(az),
      this.target.y + d * Math.sin(elev),
      this.target.z + d * ce * Math.cos(az)
    );
    camera.lookAt(this.target);
  }

  dispose() {
    this.canvas.removeEventListener('pointerdown', this._onDown);
    this.canvas.removeEventListener('pointermove', this._onMove);
    this.canvas.removeEventListener('pointerup', this._onUp);
    this.canvas.removeEventListener('pointercancel', this._onUp);
    this.canvas.removeEventListener('wheel', this._onWheel);
  }
}

/* ----------------------------------------------------------------- renderer */

class SB3DScene {
  constructor() {
    this.ready = false;
    this.sizeCm = 40;
    this.colourName = null;
    this.display = 'ground'; // 'ground' = valve down, 'hanging' = valve up
    this.fixings = { type: 'rings', pole: 'valve' };
    this.fixingCount = 0;
    /*
     * Two independent artwork slots, not one bitmap reused twice.
     *
     * "Front and back" used to mean the same decal painted at two longitudes.
     * It still can — that is the common case and it stays one setup fee — but
     * the back may now carry a genuinely different design, which is what buys
     * the second setup fee. Each slot therefore owns its own bitmap, its own
     * downsample cache and its own size/rotation, because a customer who
     * uploads two logos will want them sized independently.
     */
    this.art = {
      front: { data: null, scaled: null, transform: { sizePct: 35, rotationDeg: 0 } },
      back: { data: null, scaled: null, transform: { sizePct: 35, rotationDeg: 0 } },
    };
    this.artTwoSided = false;
    this._dirty = true;
    this._raf = null;
  }

  async init(canvas, opts = {}) {
    if (this.ready) {
      if (this.canvas === canvas) return this;
      /*
       * The section was re-rendered — the theme editor does this on every
       * setting change, and the Section Rendering API does it on cart updates.
       * The canvas we were holding is now detached, so returning early would
       * leave the new one blank forever. Tear down and rebind instead.
       */
      this.dispose();
    }
    this.canvas = canvas;
    this.opts = opts;

    /*
     * The starting size, before any geometry is built.
     *
     * The configurator used to call setSize() straight after init(), which was
     * harmless only while the default chip was 40cm and the call was a no-op.
     * With any other default it built the whole scene at 40, rebuilt the
     * fixings at the real size a moment later, and — since setSize now animates
     * — fired the grow animation on page load, so the ball popped as the page
     * appeared. Taking it here means the scene is correct first time.
     */
    if (opts.sizeCm) this.sizeCm = Number(opts.sizeCm) || this.sizeCm;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: Q.antialias,
      alpha: true,
      preserveDrawingBuffer: true, // required for toDataURL on export
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(CAM_FOV, 1, 0.1, 100);

    this.orbit = new DampedOrbit(canvas);
    this.orbit.setSizeLimits(this.sizeCm);

    // Stand-in lighting so the ball is visible while the environment map is
    // still in flight. Removed the moment PMREM hands back a real environment.
    this._tempLights = new THREE.Group();
    const hemi = new THREE.HemisphereLight(0xffffff, 0x9099a5, 1.15);
    const key = new THREE.DirectionalLight(0xffffff, 1.5);
    key.position.set(2.4, 3.2, 2.8);
    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(-3, 0.6, -1.8);
    this._tempLights.add(hemi, key, fill);
    this.scene.add(this._tempLights);

    // MeshPhysicalMaterial throughout, so the phase-7 iridescent and
    // holographic shaders can extend it via onBeforeCompile and keep PBR.
    this.ball = new THREE.Mesh(
      // Segment count is set by the silhouette, not the shading — faceting on
      // the outline is visible at full zoom with anything lower.
      new THREE.SphereGeometry(BALL_RADIUS, Q.ballSegments[0], Q.ballSegments[1]),
      new THREE.MeshPhysicalMaterial({ ...MATERIAL_FALLBACK, envMapIntensity: 1.0 })
    );
    this._attachFinishShader(this.ball.material);
    this.ball.onBeforeRender = () => {
      this._finishUniforms.uNormalMatrix.value.copy(this.ball.normalMatrix);
    };
    this.scene.add(this.ball);
    this.ground = this._buildGround();
    this.scene.add(this.ground);
    this._retargetGround(false);

    this.gores = goresForSize(this.sizeCm);
    this._applySeams();
    this._applyPanels(); // before the hardware, which borrows the ball material
    this._rebuildFixings();

    this._resize();
    this._ro = new ResizeObserver(() => this._resize());
    this._ro.observe(canvas.parentElement || canvas);

    this.ready = true;
    this._loop();

    if (opts.envUrl) await this._initEnv(opts.envUrl);
    return this;
  }

  /*
   * PMREM is the supported path for equirectangular IBL on a PBR material: it
   * prefilters the map per roughness level, which a raw equirectangular texture
   * assigned straight to envMap cannot do.
   */
  async _initEnv(url) {
    let source;
    try {
      source = await loadEquirect(url);
    } catch (err) {
      this.envError = err.message;
      return; // stand-in lights stay; the ball is dull but not broken
    }

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    pmrem.compileEquirectangularShader();
    this.envTexture = pmrem.fromEquirectangular(source.texture).texture;
    pmrem.dispose();
    source.texture.dispose(); // the float plate is large; PMREM output is not

    this.scene.environment = this.envTexture;
    this.scene.environmentIntensity = ENV.intensity;
    this.scene.environmentRotation = new THREE.Euler(0, THREE.MathUtils.degToRad(ENV.rotationDeg), 0);

    this.scene.remove(this._tempLights);
    this.envExpanded = source.expanded;
    this.envReady = true;
    this.invalidate();
  }

  _resize() {
    if (!this.renderer) return;
    const host = this.canvas.parentElement || this.canvas;
    const w = Math.max(1, Math.round(host.clientWidth || this.canvas.clientWidth || 720));
    this.renderer.setSize(w, w, false);
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
    this._dirty = true;
  }

  _loop() {
    const tick = () => {
      this._raf = requestAnimationFrame(tick);
      const now = performance.now();
      const moving = this.orbit.update();
      const glowing = this._updateHighlight(now);
      const growing = this._updateGround(now);
      const sizing = this._updateGrow(now);
      if (moving || glowing || growing || sizing || this._dirty) {
        this.orbit.applyTo(this.camera);
        this.renderer.render(this.scene, this.camera);
        this._dirty = false;
      }
    };
    this._raf = requestAnimationFrame(tick);
  }

  invalidate() {
    this._dirty = true;
  }

  /* ------------------------------------------------------------- interface */

  setSize(sizeCm) {
    const cm = Number(sizeCm) || this.sizeCm;
    if (cm === this.sizeCm) return;
    const wasCm = this.sizeCm;
    this.sizeCm = cm;
    this.orbit.setSizeLimits(cm);

    /*
     * Flake size is physically constant, so density per unit UV rises with the
     * ball: a 300cm ball shows far more, finer flecks than a 40cm one.
     */
    if (this._finishUniforms) {
      this._finishUniforms.uFlakeScale.value = FINISH.flakeScaleAt100cm * (cm / 100);
    }

    const gores = goresForSize(cm);
    if (gores !== this.gores) {
      this.gores = gores;
      this._applySeams();
      this._applyPanels(); // texel count is a function of the gore count
    }
    // Hardware is physically constant, so every size change rescales it — and
    // the count steps at the 150cm boundary.
    this._rebuildFixings();
    /*
     * The grow animation owns the ground for the duration of a size change —
     * it has to, because holding the grid still through beat one is exactly
     * what makes the ball read as growing. _retargetGround is left for the
     * cases the grow does not cover: first paint, and Ground/Hanging.
     */
    if (this.ready) this._startGrow(wasCm, cm);
    else this._retargetGround(false);
    this.invalidate();
  }

  /* ---------------------------------------------------------- artwork */

  _ensureDecal() {
    if (this.decalMesh) return;
    const cv = document.createElement('canvas');
    cv.width = DECAL.width;
    cv.height = DECAL.height;
    this._decalCanvas = cv;
    this._decalCtx = cv.getContext('2d', { willReadFrequently: true });
    this._decalImage = this._decalCtx.createImageData(DECAL.width, DECAL.height);

    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this._decalTex = tex;

    this.decalMesh = new THREE.Mesh(
      new THREE.SphereGeometry(BALL_RADIUS * DECAL.radiusScale, Q.ballSegments[0], Q.ballSegments[1]),
      new THREE.MeshPhysicalMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        metalness: 0.0,
        roughness: DECAL.inkRoughness,
        envMapIntensity: DECAL.inkEnvIntensity,
      })
    );
    this.decalMesh.renderOrder = 2;
    this.decalMesh.visible = false;
    this.scene.add(this.decalMesh);
  }

  /*
   * Paint the artwork into the equirectangular canvas.
   *
   * Inverse-mapped: we walk the canvas pixels the artwork could touch and ask
   * which part of the artwork each one sees. That leaves no sampling gaps, and
   * because the longitude difference is wrapped to [-PI, PI] it also handles a
   * logo crossing the texture seam for free — the brief's "draw it three times,
   * offset by +/- the canvas width" is a forward-drawing workaround this
   * approach does not need.
   *
   * The projection keeps equal arc length in both directions, which is what
   * stops the print distorting on the physical surface:
   *
   *   vertical arc   ay = -lat            (R = 1)
   *   horizontal arc ax = dlon * cos(lat)
   *
   * At the equator cos(lat) = 1 so the two are equal, which is why artwork
   * locked there is undistorted. The general form is here for when height
   * positioning returns.
   */
  /*
   * Artwork resampled to roughly the footprint it will occupy on the equirect
   * map, cached.
   *
   * _paintDecal samples the source bilinearly, which is right when magnifying
   * and wrong when minifying: two taps cannot represent a 1000px logo squeezed
   * into 500px of equirect, so thin strokes drop out and edges come back soft
   * and slightly aliased. Letting the browser downscale once, with real
   * filtering, puts the source near 1:1 with its destination and the bilinear
   * tap then does the job it is good at.
   *
   * Cached on the rounded target width, so dragging the size slider does not
   * rebuild it every tick — only when the footprint actually changes band.
   */
  _artForFootprint(slot) {
    const art = slot.data;
    if (!art) return null;

    const pct = clamp(slot.transform.sizePct, 1, 400) / 100;
    // Arc the artwork spans, as a fraction of the full 2*PI of the map.
    const targetW = Math.max(8, Math.round(((pct * 2) / (Math.PI * 2)) * DECAL.width));

    // Only ever downsample. Upsampling here would just blur it earlier.
    if (art.width <= targetW * 1.2) return art;

    const band = Math.round(targetW / 32) * 32; // coarse cache key
    if (slot.scaled && slot.scaled.band === band) return slot.scaled.data;

    const w = Math.max(8, band);
    const h = Math.max(1, Math.round((art.height / art.width) * w));

    const src = document.createElement('canvas');
    src.width = art.width;
    src.height = art.height;
    src.getContext('2d').putImageData(art, 0, 0);

    const dst = document.createElement('canvas');
    dst.width = w;
    dst.height = h;
    const g = dst.getContext('2d', { willReadFrequently: true });
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(src, 0, 0, w, h);

    const data = g.getImageData(0, 0, w, h);
    slot.scaled = { band, data };
    return data;
  }

  _paintDecal() {
    this._ensureDecal();
    const W = DECAL.width;
    const H = DECAL.height;
    const px = this._decalImage.data;
    px.fill(0);

    /*
     * One job per painted face. The camera faces u = 0.25 at the default view,
     * so that is where the front print goes; the back sits 180 degrees round.
     * Both land mid-gore, because gore centres sit on u = k/N and N is always
     * divisible by 4.
     *
     * When the back has its own artwork it is painted from that slot, with its
     * own size and rotation. When it does not, the front bitmap is repeated —
     * the old mirrored behaviour, which is still what "same design both sides"
     * means and still one setup fee.
     */
    const TWO_PI = Math.PI * 2;
    const jobs = [];
    if (this.art.front.data) jobs.push({ lon: ART_LON, slot: this.art.front });
    if (this.artTwoSided) {
      const backSlot = this.art.back.data ? this.art.back : this.art.front;
      if (backSlot.data) jobs.push({ lon: ART_LON + Math.PI, slot: backSlot });
    }

    if (!jobs.length) {
      this.decalMesh.visible = false;
      this._decalCtx.putImageData(this._decalImage, 0, 0);
      this._decalTex.needsUpdate = true;
      this.invalidate();
      return;
    }

    for (const job of jobs) {
      const art = this._artForFootprint(job.slot);
      if (!art) continue;

      const ad = art.data;
      const aw = art.width;
      const ah = art.height;
      const pct = clamp(job.slot.transform.sizePct, 1, 400) / 100;
      const wArc = pct * 2 * BALL_RADIUS; // "% of diameter" -> arc length
      const hArc = wArc * (ah / aw);
      const rot = THREE.MathUtils.degToRad(job.slot.transform.rotationDeg);
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);

      // Generous bounds: the diagonal covers every rotation.
      const halfDiag = 0.5 * Math.hypot(wArc, hArc);
      const latMax = Math.min(halfDiag, Math.PI / 2 - 1e-3);
      const y0 = Math.max(0, Math.floor(((Math.PI / 2 - latMax) / Math.PI) * H) - 2);
      const y1 = Math.min(H, Math.ceil(((Math.PI / 2 + latMax) / Math.PI) * H) + 2);
      const lonHalf = Math.min(Math.PI, halfDiag / Math.max(Math.cos(latMax), 0.05));
      const xSpan = Math.ceil((lonHalf / (Math.PI * 2)) * W) + 2;

      const lon0 = job.lon;
      const xc = Math.round((lon0 / TWO_PI) * W);
      for (let y = y0; y < y1; y++) {
        const lat = Math.PI / 2 - ((y + 0.5) / H) * Math.PI;
        const cosLat = Math.cos(lat);
        if (cosLat < 1e-4) continue;
        const ay = -lat;
        const rowBase = y * W;

        for (let dx = -xSpan; dx <= xSpan; dx++) {
          const xi = (((xc + dx) % W) + W) % W;
          let dlon = ((xi + 0.5) / W) * TWO_PI - lon0;
          dlon = (((dlon + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI - Math.PI;

          const ax = dlon * cosLat;
          const au = ax * cosR + ay * sinR;
          const av = -ax * sinR + ay * cosR;

          const sx = (au / wArc + 0.5) * aw - 0.5;
          const sy = (av / hArc + 0.5) * ah - 0.5;
          if (sx < 0 || sy < 0 || sx >= aw - 1 || sy >= ah - 1) continue;

          // Bilinear — nearest-neighbour aliases vector edges badly.
          const fx = sx | 0;
          const fy = sy | 0;
          const tx = sx - fx;
          const ty = sy - fy;
          const i00 = (fy * aw + fx) * 4;
          const i10 = i00 + 4;
          const i01 = i00 + aw * 4;
          const i11 = i01 + 4;
          const w00 = (1 - tx) * (1 - ty);
          const w10 = tx * (1 - ty);
          const w01 = (1 - tx) * ty;
          const w11 = tx * ty;

          const a = ad[i00 + 3] * w00 + ad[i10 + 3] * w10 + ad[i01 + 3] * w01 + ad[i11 + 3] * w11;
          if (a < 2) continue;

          const o = (rowBase + xi) * 4;
          px[o] = ad[i00] * w00 + ad[i10] * w10 + ad[i01] * w01 + ad[i11] * w11;
          px[o + 1] = ad[i00 + 1] * w00 + ad[i10 + 1] * w10 + ad[i01 + 1] * w01 + ad[i11 + 1] * w11;
          px[o + 2] = ad[i00 + 2] * w00 + ad[i10 + 2] * w10 + ad[i01 + 2] * w01 + ad[i11 + 2] * w11;
          px[o + 3] = a;
        }
      }
    }

    this._decalCtx.putImageData(this._decalImage, 0, 0);
    this._decalTex.needsUpdate = true;
    this.decalMesh.visible = true;
    this.invalidate();
  }

  /*
   * Iridescent and holographic, as one shader on the ball material.
   *
   * Which finish applies is read per-fragment from the panel ORM map's red
   * channel, so a two-tone ball can carry a shader finish on one set of gores
   * and a plain tint on the other without a second material or a split mesh.
   *
   * Built with onBeforeCompile so MeshPhysicalMaterial's PBR lighting stays
   * intact — the alternative is reimplementing image-based lighting by hand.
   */
  _attachFinishShader(material) {
    /*
     * The second ripple octave is compiled out entirely on the mobile tier
     * rather than multiplied by zero — a branch or a zero multiply still costs
     * the six noise taps and the fwidth, which is the whole expense.
     */
    const SB_RIPPLE_FADE2 =
      Q.rippleOctaves > 1
        ? '  float sbFade2 = 1.0 - smoothstep( 0.35, 1.0, uRippleFreq * 2.3 * sbFoot );'
        : '';
    const SB_RIPPLE_OCTAVE2 =
      Q.rippleOctaves > 1
        ? '\n           + 0.45 * sbNoiseGrad( sbP * uRippleFreq * 2.3 + 11.7, 0.35 ) * sbFade2'
        : '';

    this._iridLut = this._iridLut || buildIridescentLut();
    this._flakeNoise = this._flakeNoise || buildFlakeNoise(FINISH.flakeLit, FINISH.flakeRadius);

    const uniforms = {
      uIridLut: { value: this._iridLut },
      uIridStrength: { value: FINISH.iridStrength },
      uIridMilk: { value: FINISH.iridMilk },
      uIridAlpha: { value: FINISH.iridAlpha },
      uNoise: { value: this._flakeNoise },
      uFlakeScale: { value: FINISH.flakeScaleAt100cm },
      uFlakeShift: { value: FINISH.flakeShift },
      uSparkle: { value: FINISH.sparkle },
      uSparkleGate: { value: FINISH.sparkleGate },
      uRippleAmp: { value: RIPPLE.amplitude },
      uRippleFreq: { value: RIPPLE.frequency },
      // Object -> view rotation, refreshed per draw so the ripple stays welded
      // to the surface as the camera orbits instead of swimming with it.
      uNormalMatrix: { value: new THREE.Matrix3() },
    };
    this._finishUniforms = uniforms;

    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);

      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vSbPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vSbPos = position;');

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
uniform sampler2D uIridLut;
uniform float uIridStrength;
uniform float uIridMilk;
uniform float uIridAlpha;
uniform sampler2D uNoise;
uniform float uFlakeScale;
uniform float uFlakeShift;
uniform float uSparkle;
uniform float uSparkleGate;
uniform float uRippleAmp;
uniform float uRippleFreq;
uniform mat3 uNormalMatrix;
varying vec3 vSbPos;

// Value noise (Inigo Quilez's formulation) — cheap, smooth, and isotropic.
float sbHash( vec3 p ) {
  p = fract( p * 0.3183099 + vec3( 0.1, 0.2, 0.3 ) );
  p *= 17.0;
  return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
}
float sbNoise( vec3 x ) {
  vec3 i = floor( x );
  vec3 f = fract( x );
  f = f * f * ( 3.0 - 2.0 * f );
  return mix(
    mix( mix( sbHash( i + vec3( 0.0, 0.0, 0.0 ) ), sbHash( i + vec3( 1.0, 0.0, 0.0 ) ), f.x ),
         mix( sbHash( i + vec3( 0.0, 1.0, 0.0 ) ), sbHash( i + vec3( 1.0, 1.0, 0.0 ) ), f.x ), f.y ),
    mix( mix( sbHash( i + vec3( 0.0, 0.0, 1.0 ) ), sbHash( i + vec3( 1.0, 0.0, 1.0 ) ), f.x ),
         mix( sbHash( i + vec3( 0.0, 1.0, 1.0 ) ), sbHash( i + vec3( 1.0, 1.0, 1.0 ) ), f.x ), f.y ), f.z );
}
vec3 sbNoiseGrad( vec3 p, float e ) {
  return vec3(
    sbNoise( p + vec3( e, 0.0, 0.0 ) ) - sbNoise( p - vec3( e, 0.0, 0.0 ) ),
    sbNoise( p + vec3( 0.0, e, 0.0 ) ) - sbNoise( p - vec3( 0.0, e, 0.0 ) ),
    sbNoise( p + vec3( 0.0, 0.0, e ) ) - sbNoise( p - vec3( 0.0, 0.0, e ) ) );
}
`
        )
        /*
         * Thin-film hue goes onto diffuseColor before lighting. For a metal
         * that value is the specular tint, so the environment reflection picks
         * the hue up rather than it sitting on top as a flat wash.
         */
        /*
         * Undulation, applied after the seam normal map so it rides on top of
         * the welds rather than replacing them. The gradient is taken in object
         * space, projected onto the tangent plane so it only tilts the surface
         * rather than inflating it, then rotated into view space to match the
         * normal three has already computed.
         */
        .replace(
          '#include <normal_fragment_maps>',
          `#include <normal_fragment_maps>
{
  vec3 sbP = normalize( vSbPos );

  /*
   * Procedural noise has no mipmaps, so once a cell falls below about a pixel
   * it stops being texture and starts being crawling noise. Fade each octave
   * out on its own screen-space footprint — at this frequency the second octave
   * is sub-pixel at the default framing and would shimmer constantly.
   */
  float sbFoot = max( fwidth( sbP.x ), max( fwidth( sbP.y ), fwidth( sbP.z ) ) );
  float sbFade1 = 1.0 - smoothstep( 0.35, 1.0, uRippleFreq * sbFoot );
${SB_RIPPLE_FADE2}

  vec3 sbG = sbNoiseGrad( sbP * uRippleFreq, 0.35 ) * sbFade1${SB_RIPPLE_OCTAVE2};
  vec3 sbT = sbG - sbP * dot( sbG, sbP );
  normal = normalize( normal - uNormalMatrix * sbT * uRippleAmp );
}`
        )
        .replace(
          '#include <lights_physical_fragment>',
          `
{
  float sbFinish = texture2D( roughnessMap, vMapUv ).r;
  if ( sbFinish > 0.25 && sbFinish < 0.75 ) {
    vec3 sbV = normalize( vViewPosition );
    float sbNdV = clamp( dot( normalize( normal ), sbV ), 0.0, 1.0 );
    vec3 sbHue = texture2D( uIridLut, vec2( sbNdV, 0.5 ) ).rgb;
    vec3 sbTint = diffuseColor.rgb * mix( vec3( 1.0 ), sbHue, uIridStrength );
    diffuseColor.rgb = mix( sbTint, vec3( 0.88 ), uIridMilk );
    /*
     * Survives only because the material is flagged transparent for iridescent
     * balls — opaque_fragment forces alpha back to 1.0 under #ifdef OPAQUE,
     * which three defines whenever material.transparent is false.
     */
    diffuseColor.a = uIridAlpha;
  }
}
#include <lights_physical_fragment>`
        )
        /*
         * Sparkle is added after lighting and gated on the SPECULAR term, not
         * total brightness: flecks only fire where light actually catches them.
         * Ungated it reads as noise rather than glitter.
         */
        .replace(
          '#include <opaque_fragment>',
          `
{
  float sbFinish = texture2D( roughnessMap, vMapUv ).r;
  if ( sbFinish >= 0.75 ) {
    float sbSpec = dot( totalSpecular, vec3( 0.2126, 0.7152, 0.0722 ) );
    float sbGate = smoothstep( uSparkleGate, uSparkleGate * 8.0, sbSpec );
    vec2 sbUv = vMapUv * uFlakeScale;
    vec4 sbN = texture2D( uNoise, sbUv );
    /*
     * R is coverage, already thresholded at build time, so this is a plain read
     * — no step(). Thresholding here instead would be thresholding a mipped
     * average, which is what made the flecks disappear.
     */
    float sbFleck = sbN.r;
    /*
     * Diffraction: the fleck hue rotates with the view angle. A real
     * holographic facet changes colour as the ball turns; a fixed per-fleck
     * hue reads as static confetti stuck to the surface.
     */
    float sbFlakeNdV = clamp( dot( normalize( normal ), normalize( vViewPosition ) ), 0.0, 1.0 );
    float sbPhase = sbN.b + uFlakeShift * sbFlakeNdV;
    vec3 sbHue = 0.5 + 0.5 * cos( 6.28318 * ( sbN.g + vec3( 0.0, 0.33, 0.67 ) ) + sbPhase * 6.28318 );
    // Fade the flecks out once the noise shrinks below a pixel, or the sparkle
    // aliases into crawling noise — the mobile failure the brief warns about.
    float sbD = max( fwidth( sbUv.x ), fwidth( sbUv.y ) );
    float sbAA = 1.0 - smoothstep( 0.35, 1.2, sbD );
    outgoingLight += sbHue * sbFleck * sbGate * sbAA * uSparkle;
  }
}
#include <opaque_fragment>`
        );
    };
    material.needsUpdate = true;
  }

  /* --------------------------------------------------------- hardware */

  /*
   * Cartoon-style outline stroke on the hardware, replacing the Fresnel rim
   * glow this used to be.
   *
   * The rim was the wrong instrument. It brightened the whole fitting rather
   * than drawing its edge — and it had to, because a pure Fresnel term is
   * nearly invisible on parts this small, so it carried a 0.45 floor and an
   * opacity lift on top. The result read as the part being blasted white, which
   * says "something changed here" but not "the fitting is THIS shape, THERE".
   *
   * An expanded back-faced shell is the classic outline trick and needs no
   * post-processing, so EffectComposer stays un-vendored. The expansion happens
   * in CLIP space along the projected normal, which makes the stroke a constant
   * number of pixels wide instead of a constant number of world units — the
   * fixings preset sits several times closer than the default view, and a
   * world-space offset would be a hairline in one and a slab in the other.
   */
  _outlineMaterial() {
    if (this._hlOutlineMat) return this._hlOutlineMat;
    if (!this._highlightUniform) this._highlightUniform = { value: 0 };
    const c = HIGHLIGHT.colour;
    this._hlOutlineMat = new THREE.ShaderMaterial({
      uniforms: {
        uHighlight: this._highlightUniform,
        uPx: { value: HIGHLIGHT.strokePx },
        uViewport: { value: new THREE.Vector2(1, 1) },
        uColour: { value: new THREE.Color(c[0], c[1], c[2]) },
      },
      vertexShader: `
uniform float uPx;
uniform vec2 uViewport;
void main() {
  vec4 clip = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  vec3 vn = normalize( normalMatrix * normal );
  // Project the normal and push the vertex along it by a pixel amount. The
  // * clip.w undoes the perspective divide that is about to happen.
  vec3 cn = normalize( ( projectionMatrix * vec4( vn, 0.0 ) ).xyz );
  clip.xy += cn.xy * ( uPx * 2.0 / uViewport ) * clip.w;
  gl_Position = clip;
}`,
      fragmentShader: `
uniform vec3 uColour;
uniform float uHighlight;
void main() {
  gl_FragColor = vec4( uColour, uHighlight );
}`,
      side: THREE.BackSide,
      transparent: true,
      // Never write depth: the shell sits proud of the real part and would
      // otherwise occlude the very thing it is outlining.
      depthWrite: false,
      toneMapped: false,
    });
    return this._hlOutlineMat;
  }

  /*
   * A shell of the whole fixings group, sharing its geometry and transforms.
   * Cloning the group rather than rebuilding it keeps the two in lockstep — a
   * hand-built parallel hierarchy would drift the moment the fixings changed.
   */
  _buildHighlightOutline(grp) {
    const shell = grp.clone(true);
    const mat = this._outlineMaterial();
    const drop = [];
    shell.traverse((o) => {
      if (!o.isMesh) return;
      /*
       * Dropped: the embossed collar rings, which would ring the valve in green
       * a dozen times over and bury the thing being pointed at (exactly as they
       * buried the line drawing), and anything flagged sbNoOutline — open
       * surfaces and interior detail, which a back-faced hull cannot outline.
       */
      if (o.userData.sbDetailOnly || o.userData.sbNoOutline) {
        drop.push(o);
        return;
      }
      // Proxies exist only in here.
      if (o.userData.sbOutlineProxy) o.visible = true;
      o.material = mat;
    });
    for (const o of drop) o.parent?.remove(o);
    shell.visible = false;
    shell.renderOrder = 3; // after the ball and the decal
    return shell;
  }

  /*
   * Drive the highlight. Returns true while it still needs frames, so the
   * render loop keeps ticking through the hold and the fade.
   */
  _updateHighlight(now) {
    const u = this._highlightUniform;
    if (!u) return false;

    // The stroke is measured in pixels, so it needs the live drawing buffer
    // size — not the CSS size, which is wrong by the device pixel ratio.
    if (this._hlOutlineMat && this.renderer) {
      const v = new THREE.Vector2();
      this.renderer.getDrawingBufferSize(v);
      this._hlOutlineMat.uniforms.uViewport.value.copy(v);
    }
    if (this._hwOutline) this._hwOutline.visible = u.value > 0.001;

    const end = this._highlightUntil || 0;
    if (!end) return false;
    const dt = now - end;
    if (dt < 0) {
      u.value = 1;
      return true;
    }
    if (dt < HIGHLIGHT.fadeMs) {
      u.value = 1 - dt / HIGHLIGHT.fadeMs;
      return true;
    }
    u.value = 0;
    this._highlightUntil = 0;
    return true; // one last frame to clear it
  }

  _pulseHighlight() {
    if (!this._highlightUniform) this._highlightUniform = { value: 0 };
    this._highlightUntil = performance.now() + HIGHLIGHT.holdMs;
    this._highlightUniform.value = 1;
    this.invalidate();
  }

  _hardwareMaterials() {
    if (this._hwMats) return this._hwMats;
    this._hwMats = {
      // Opaque grey plastic — the valve.
      grey: new THREE.MeshPhysicalMaterial({
        color: 0x9b9fa3,
        roughness: 0.44,
        metalness: 0.0,
        clearcoat: 0.3,
        clearcoatRoughness: 0.32,
        // The lathed valve body is an open annulus; double-siding it avoids the
        // inside of the 3cm opening dropping out when normals face outward.
        side: THREE.DoubleSide,
      }),
      /*
       * Translucent plastic — the ring tabs. Deliberately opacity-based rather
       * than MeshPhysicalMaterial transmission: transmission forces an extra
       * render pass per frame, which is a poor trade for parts this small on
       * the mid-range Android the brief targets.
       */
      translucent: new THREE.MeshPhysicalMaterial({
        color: 0xdfe4e8,
        roughness: 0.3,
        metalness: 0.0,
        clearcoat: 0.6,
        clearcoatRoughness: 0.18,
        transparent: true,
        opacity: 0.66,
      }),
      // Stainless — the D-rings.
      steel: new THREE.MeshPhysicalMaterial({ color: 0xd9dbdf, roughness: 0.22, metalness: 1.0 }),
      // White interior of the exhaust ports.
      portWhite: new THREE.MeshPhysicalMaterial({ color: 0xf4f4f2, roughness: 0.8, metalness: 0.0 }),
      // Shadowed floor inside the valve opening.
      dark: new THREE.MeshPhysicalMaterial({ color: 0x3c3f42, roughness: 0.72, metalness: 0.0 }),
      // Welded discs and patches are the ball's own skin, so they inherit its
      // finish; only the standoff rim catches light differently.
      weld: null,
    };
    /*
     * Nothing to inject any more — the highlight is a separate outline shell,
     * so the hardware materials are left exactly as authored.
     */
    return this._hwMats;
  }

  /* Untextured stand-in for the ball's finish, for parts without sphere UVs. */
  _weldSkinMaterial() {
    const spec = materialSpecFor(this.colourName);
    if (!this._weldSkin) this._weldSkin = new THREE.MeshPhysicalMaterial();
    this._weldSkin.color.setHex(spec.color);
    this._weldSkin.roughness = spec.roughness;
    this._weldSkin.metalness = spec.metalness;
    this._weldSkin.needsUpdate = true;
    return this._weldSkin;
  }

  _rebuildFixings() {
    const mats = this._hardwareMaterials();
    if (this._hwOutline) {
      this.scene.remove(this._hwOutline);
      this._hwOutline = null; // geometry is shared with fixtures, disposed below
    }
    if (this.fixtures) {
      this.scene.remove(this.fixtures);
      this.fixtures.traverse((o) => o.geometry && o.geometry.dispose());
    }
    const grp = new THREE.Group();
    this.fixtures = grp;

    const cm = (v) => (v * 2) / this.sizeCm; // real centimetres -> world units
    const skin = this.ball.material; // welded discs share the ball's finish
    const weldH = cm(FIX.weldHeightCm);
    const tube = cm(FIX.tubeDiameterCm) / 2;
    // Arc distance -> angle from the pole: theta = s / R, with R in world units.
    const mountLat = Math.PI / 2 - cm(mountRadiusCmFor(this.sizeCm)) / BALL_RADIUS;

    // Which pole carries the valve: 'hanging' puts it up, 'ground' puts it down.
    const valveSign = this.display === 'hanging' ? 1 : -1;
    const fixSign =
      this.fixings.type === 'drings' && this.fixings.pole === 'opposite' ? -valveSign : valveSign;

    /*
     * Valve assembly, always on the valve pole. Built to Zac's measurements off
     * a real ball plus the close-up photo: a flat 6cm valve standing only 8mm
     * proud with an open 3cm centre, a three-lobed twist grip, a tether strap
     * lying on the skin, concentric embossed rings in the surrounding material,
     * and two white exhaust ports.
     */
    const valve = new THREE.Group();
    const vR = cm(FIX.valveDiameterCm) / 2;
    const holeR = cm(FIX.valveHoleDiameterCm) / 2;
    const vH = cm(FIX.valveHeightCm);
    const collarR = cm(FIX.valveDiameterCm / 2 + FIX.valveCollarWidthCm);

    /*
     * The collar and its concentric rings are welded to the skin, and they are
     * wide enough that they have to follow its curvature. Built as flat discs on
     * the tangent plane at the pole they lift clear of the surface — 0.8cm at
     * the outer ring on an 80cm ball, 1.7cm on a 40cm one — and read as a saucer
     * hovering above the top rather than material wrapped onto it.
     *
     * So they are spherical shells concentric with the ball. Only rotated into
     * place, never translated, which is what keeps them lying on the skin.
     */
    const capDetail = new THREE.Group();
    if (valveSign < 0) capDetail.rotation.x = Math.PI;
    const collarAng = collarR / BALL_RADIUS; // surface arc -> angle from the pole

    capDetail.add(
      new THREE.Mesh(
        applySkinLatitudeUV(
          new THREE.SphereGeometry(BALL_RADIUS + weldH * 0.4, 96, 28, 0, Math.PI * 2, 0, collarAng)
        ),
        skin
      )
    );

    /*
     * Concentric embossed rings, as thin raised spherical bands. The photo shows
     * a whole family of them running well past the collar, not the two faint
     * ones originally guessed — they are much of what makes the valve area
     * recognisable.
     */
    for (const f of [1.34, 1.18, 1.04, 0.92, 0.8, 0.69]) {
      const a = collarAng * f;
      const half = (weldH * 1.1) / BALL_RADIUS;
      const emboss = new THREE.Mesh(
        applySkinLatitudeUV(
          new THREE.SphereGeometry(BALL_RADIUS + weldH, 128, 2, 0, Math.PI * 2, a - half, half * 2)
        ),
        skin
      );
      /*
       * Surface decoration, not structure. Six raised bands contribute twelve
       * concentric circles to an outline drawing, which swamps the valve and
       * the release holes — the things the drawing exists to show. Kept in the
       * product render, dropped from the line art.
       */
      emboss.userData.sbDetailOnly = true;
      capDetail.add(emboss);
    }
    grp.add(capDetail);

    /*
     * Body as a lathed annulus: outer wall up, flat top, then back down the
     * inside of the 3cm opening. A plain cylinder cannot express the open
     * centre, which is the valve's most recognisable feature.
     */
    const prof = [
      [holeR, 0],
      [holeR, vH * 0.86],
      [holeR * 1.1, vH],
      [vR * 0.92, vH],
      [vR, vH * 0.76],
      [vR, 0],
    ].map(([x, y]) => new THREE.Vector2(x, y));
    const body = new THREE.Mesh(new THREE.LatheGeometry(prof, 64), mats.grey);
    body.position.y = weldH;
    body.userData.sbNoOutline = true;
    valve.add(body);

    // Recessed floor, so the opening reads as a hole rather than a grey disc.
    const floor = new THREE.Mesh(new THREE.CircleGeometry(holeR, 44), mats.dark);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = weldH + vH * 0.16;
    floor.userData.sbNoOutline = true;
    valve.add(floor);

    // Three-lobed twist grip spanning the opening.
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(holeR * 0.34, holeR * 0.34, vH * 0.7, 20), mats.grey);
    hub.position.y = weldH + vH * 0.5;
    hub.userData.sbNoOutline = true;
    valve.add(hub);
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const blade = new THREE.Mesh(
        new THREE.BoxGeometry(holeR * 0.92, vH * 0.5, holeR * 0.42),
        mats.grey
      );
      blade.position.set(Math.cos(a) * holeR * 0.5, weldH + vH * 0.58, Math.sin(a) * holeR * 0.5);
      blade.rotation.y = -a;
      blade.userData.sbNoOutline = true;
      valve.add(blade);
    }

    /*
     * Invisible stand-in whose only job is to be outlined.
     *
     * The highlight shell needs ONE closed silhouette per fitting. Outlining
     * the valve's real parts gives the opposite: the hub and the three grip
     * blades are closed boxes sitting INSIDE the opening, so each got its own
     * stroke and the valve filled with green slashes; the lathed body and the
     * flat floor are open surfaces, where a back-faced hull has no consistent
     * outside and draws garbage.
     *
     * A plain closed cylinder at the valve's real outer diameter and height
     * produces the single clean ring the fitting actually wants. Never rendered
     * itself — _buildHighlightOutline is the only thing that makes it visible.
     */
    const outlineProxy = new THREE.Mesh(
      new THREE.CylinderGeometry(vR, vR, vH, 48),
      mats.grey
    );
    outlineProxy.position.y = weldH + vH * 0.5;
    outlineProxy.visible = false;
    outlineProxy.userData.sbOutlineProxy = true;
    valve.add(outlineProxy);

    /*
     * The cap's tether strap is deliberately NOT modelled.
     *
     * It exists on the real ball — the close-up shows a light loop running from
     * the cap out to an anchor on the collar — but a plain torus was a poor
     * stand-in for it: an offset ring on the valve's tangent plane, so it floated
     * clear of the curved skin and left the valve sitting off-centre inside it.
     * Doing it properly means a strap that curves from the cap down to a fixed
     * anchor and conforms to the surface. Until that exists, nothing is more
     * accurate here than a wrong ring.
     */

    // Seated, not perched: without this the 6cm valve's rim floats clear of the
    // curve — 2.3mm on a 40cm ball, plainly visible at a grazing angle.
    placeOnSphere(valve, (Math.PI / 2) * valveSign, 0, BALL_RADIUS - sagittaFor(vR));
    grp.add(valve);

    /*
     * Two exhaust ports, white inside. Seated on the sphere in their own right
     * rather than on the valve's tangent plane — at 5cm out they would float
     * clear of the skin on the smaller sizes, the same defect as the collar.
     *
     * Flat discs rather than standing cylinders: the ports are a couple of
     * millimetres proud in reality, which at 100cm is 0.004 world units, so a
     * cylinder is invisibly flush against the collar.
     */
    const exR = cm(FIX.exhaustHoleDiameterCm) / 2;
    const exAng =
      cm(FIX.valveDiameterCm / 2 + FIX.exhaustHoleGapCm + FIX.exhaustHoleDiameterCm / 2) / BALL_RADIUS;
    for (let i = 0; i < FIX.exhaustHoleCount; i++) {
      const lon = (i / FIX.exhaustHoleCount) * Math.PI * 2 + Math.PI * 0.25;
      const port = new THREE.Group();
      const face = new THREE.Mesh(new THREE.CircleGeometry(exR, 28), mats.portWhite);
      face.rotation.x = -Math.PI / 2;
      face.position.y = weldH * 1.2;
      port.add(face);
      const portRim = new THREE.Mesh(new THREE.TorusGeometry(exR, exR * 0.16, 8, 28), mats.grey);
      portRim.rotation.x = Math.PI / 2;
      portRim.position.y = weldH * 1.15;
      port.add(portRim);
      placeOnSphere(port, (Math.PI / 2 - exAng) * valveSign, lon, BALL_RADIUS);
      grp.add(port);
    }

    /* ---- fixings ---- */
    const isRings = this.fixings.type !== 'drings';
    const count = isRings ? ringCountFor(this.sizeCm) : dRingCountFor(this.sizeCm);
    this.fixingCount = count;

    // A single fixing on the bare pole sits centred — that is how one hang
    // point actually works. Anything else spaces evenly around the pole.
    const centred = !isRings && count === 1 && fixSign !== valveSign;

    for (let i = 0; i < count; i++) {
      /*
       * Half a step off the artwork, in MOUNT coordinates, so the print always
       * falls midway between two fixings: 90 and 270 degrees for a pair, 45 /
       * 135 / 225 / 315 for four. Measured as world azimuths, not derived on
       * paper — see ART_LON_MOUNT.
       */
      const lon = centred ? 0 : ART_LON_MOUNT + ((i + 0.5) / count) * Math.PI * 2;
      const lat = centred ? (Math.PI / 2) * fixSign : mountLat * fixSign;
      const discR = cm(isRings ? FIX.tabDiscDiameterCm : FIX.dRingDiscDiameterCm) / 2;
      const discAng = discR / BALL_RADIUS;
      const discMat = isRings ? mats.translucent : this._weldSkinMaterial();

      /*
       * The welded disc follows the curvature — it is bonded film, not a rigid
       * plate. Built as a spherical cap concentric with the ball and only
       * ROTATED into place, never translated, exactly like the valve collar. As
       * a flat cylinder its rim floated 1.9mm clear on a 40cm ball, and because
       * the tabs sit 43 degrees from the pole there they are seen at precisely
       * the grazing angle that makes such a gap obvious.
       */
      const shell = new THREE.Mesh(
        new THREE.SphereGeometry(
          BALL_RADIUS + weldH * 0.4, 48, 14, 0, Math.PI * 2, 0, discAng
        ),
        discMat
      );
      placeOnSphere(shell, lat, lon, 0); // rotation only
      grp.add(shell);

      // Welded edge, as a raised band at the cap's rim. This is what makes a
      // clear disc legible against the mirror finish.
      const bandHalf = (weldH * 1.3) / BALL_RADIUS;
      const band = new THREE.Mesh(
        new THREE.SphereGeometry(
          BALL_RADIUS + weldH, 64, 2, 0, Math.PI * 2,
          Math.max(0, discAng - bandHalf), bandHalf * 2
        ),
        discMat
      );
      placeOnSphere(band, lat, lon, 0);
      grp.add(band);

      // The fitting itself is rigid, so it stays on the tangent plane — but
      // seated by its own footprint so its base meets the disc.
      const mount = new THREE.Group();
      let footprint;

      if (isRings) {
        /*
         * A low clear lug, not a tall standing loop. The photo shows a small
         * moulded eye sitting close to the skin on its own clear disc — an
         * upright ring would be several times too prominent.
         */
        const loopR = cm(FIX.tabLoopDiameterCm) / 2;
        footprint = loopR * 1.15;
        const pedestal = new THREE.Mesh(
          new THREE.CylinderGeometry(loopR * 0.95, loopR * 1.15, cm(FIX.tabPedestalHeightCm), 28),
          mats.translucent
        );
        pedestal.position.y = weldH + cm(FIX.tabPedestalHeightCm) / 2;
        mount.add(pedestal);

        const eye = new THREE.Mesh(new THREE.TorusGeometry(loopR, tube * 0.75, 10, 40), mats.translucent);
        eye.position.y = weldH + cm(FIX.tabPedestalHeightCm) + loopR * 0.55;
        mount.add(eye);
      } else {
        const dR = cm(FIX.dRingDiameterCm) / 2;
        footprint = dR;
        const dring = new THREE.Mesh(makeDRingGeometry(dR, tube), mats.steel);
        dring.position.y = weldH + tube * 0.4;
        mount.add(dring);
      }

      placeOnSphere(mount, lat, lon, BALL_RADIUS - sagittaFor(footprint));
      grp.add(mount);
    }

    this.scene.add(grp);

    this._hwOutline = this._buildHighlightOutline(grp);
    this.scene.add(this._hwOutline);
    this.invalidate();
  }

  _applySeams() {
    const m = this.ball.material;
    m.normalMap = buildSeamNormalMap(this.gores);
    m.normalScale.set(SEAM.normalScale, SEAM.normalScale);
    if (this.renderer) {
      m.normalMap.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    }
    m.needsUpdate = true;
  }

  /*
   * `secondary` is an addition to the brief's signature: pass a second colour
   * name to alternate it across the gores, or omit it for a single-colour ball.
   */
  setColour(colourName, secondary) {
    this.colourName = colourName;
    this.secondColourName = secondary || null;
    this._applyPanels();
  }

  _applyPanels() {
    const specA = materialSpecFor(this.colourName);
    const specB = this.secondColourName ? materialSpecFor(this.secondColourName) : specA;
    const maps = buildPanelMaps(this.gores || goresForSize(this.sizeCm), specA, specB);

    const m = this.ball.material;
    this._panelMaps?.colour.dispose();
    this._panelMaps?.orm.dispose();
    this._panelMaps = maps;

    /*
     * Tint, roughness and metalness all come from the maps, so the base values
     * are left at 1 — three multiplies material value by texel. One code path
     * serves both single and two-tone balls.
     */
    m.color.setHex(0xffffff);
    m.map = maps.colour;
    m.roughnessMap = maps.orm;
    m.metalnessMap = maps.orm;
    m.roughness = 1.0;
    m.metalness = 1.0;
    /*
     * Scoped to the colours actually on the ball: a plain mirror stays in the
     * opaque pass, where it belongs. Flipping this flag changes the shader
     * defines, so it has to precede needsUpdate.
     */
    m.transparent = specA.finish === 'iridescent' || specB.finish === 'iridescent';
    m.needsUpdate = true;
    if (this.fixtures) this._weldSkinMaterial(); // keep welded discs in step
    this.invalidate();
  }

  /*
   * Accepts a canvas, an ImageData, or null to clear. The bitmap is read once
   * and cached — repainting on every slider tick must not re-read the source.
   */
  setArtwork(source, opts = {}) {
    /*
     * opts.slot picks which face this bitmap belongs to — 'front' by default,
     * so every existing call keeps its meaning. opts.twoSided is only read for
     * the front, because it describes the job and not the bitmap: letting a
     * back upload flip it would mean the act of adding a second design silently
     * turned on two-sided print.
     */
    const which = opts.slot === 'back' ? 'back' : 'front';
    const slot = this.art[which];
    const had = !!this.art.front.data;
    if (which === 'front' && opts.twoSided != null) this.artTwoSided = !!opts.twoSided;
    slot.scaled = null; // the footprint cache belongs to the old bitmap

    if (!source) {
      slot.data = null;
    } else if (source.data && source.width) {
      slot.data = source; // already ImageData
    } else if (source.getContext) {
      const g = source.getContext('2d', { willReadFrequently: true });
      slot.data = g.getImageData(0, 0, source.width, source.height);
    } else {
      slot.data = null;
    }
    if (this.ready) this._paintDecal();

    /*
     * Swing round to face the print the moment artwork arrives. The customer
     * may have orbited anywhere before uploading, and landing on the back of
     * the ball at the exact moment their logo appears reads as the upload
     * having failed. Animated, not snapped — this one is for a person watching.
     * Only on the transition into having artwork, so it does not fight them
     * every time a slider moves.
     */
    if (this.ready && which === 'front' && !had && slot.data) this.setView('default');
    /*
     * A back upload swings round to show it, for the same reason the front
     * does: the decal lands on the far side of the ball, and not moving reads
     * as the upload having failed.
     */
    if (this.ready && which === 'back' && slot.data) this.setView('back');
  }

  setArtworkTransform({ sizePct, rotationDeg } = {}, opts = {}) {
    const slot = this.art[opts.slot === 'back' ? 'back' : 'front'];
    if (sizePct != null && Number(sizePct) !== slot.transform.sizePct) slot.scaled = null;
    if (sizePct != null) slot.transform.sizePct = Number(sizePct) || slot.transform.sizePct;
    if (rotationDeg != null) slot.transform.rotationDeg = Number(rotationDeg) || 0;
    if (this.ready && slot.data) this._paintDecal();
  }

  /*
   * `display` is an addition to the documented shape: it is what decides which
   * pole carries the valve, so this is its natural home. Optional — omit it and
   * the current orientation is kept.
   */
  setFixings({ type, pole, display } = {}) {
    if (type) this.fixings.type = type === 'drings' ? 'drings' : 'rings';
    if (pole) this.fixings.pole = pole === 'opposite' ? 'opposite' : 'valve';
    if (display) this.display = display === 'hanging' ? 'hanging' : 'ground';
    if (this.ready) this._rebuildFixings();
    // Ground vs Hanging finally looks like what it says: the ball either rests
    // on the floor or is lifted off it, with the shadow spreading to match.
    if (this.ready) this._retargetGround(true);
  }

  fixingSummary() {
    return {
      type: this.fixings.type,
      pole: this.fixings.pole,
      count: this.fixings.type === 'drings' ? dRingCountFor(this.sizeCm) : ringCountFor(this.sizeCm),
    };
  }

  setView(preset, opts = {}) {
    if (!this.orbit) return;
    if (preset === 'fixings') {
      /*
       * Aim at whichever pole actually carries the valve, and move in closer —
       * the hardware is physically small, so on a 300cm ball the default
       * distance would render it as a few pixels. A valve-down ball needs a
       * view from below the equator, outside the free-orbit clamp; that is
       * allowed because this is a fixed preset whose job is a flattering,
       * legible framing, and it is what generates the spec-sheet diagram.
       */
      const sign = this.display === 'hanging' ? 1 : -1;
      /*
       * Frame the fixings without letting the ball stop looking like a ball.
       *
       * Sizing purely so the fixing cluster fills a fixed slice of the viewport
       * breaks down at both ends: on a 40cm ball the cluster is 75% of the
       * ball's own diameter, so the camera retreats until the ball is barely
       * half the frame, and on a 300cm ball it comes so close the ball is four
       * times the frame. Across the size range that reads as the preview
       * jumping around. The cluster rule sets the intent; the clamp keeps the
       * ball between roughly two-thirds and one-and-a-half frames.
       */
      const clusterWorld = (4 * mountRadiusCmFor(this.sizeCm)) / this.sizeCm;
      const frameFraction = 0.42;
      const ideal =
        clusterWorld / (frameFraction * 2 * Math.tan(THREE.MathUtils.degToRad(CAM_FOV) / 2));
      const dist = clamp(ideal, 2.2, 5.0);
      this._pulseHighlight();
      this.orbit.goTo('fixings', {
        elev: VIEWS.fixings.elev * sign,
        dist,
        /*
         * Lean toward the valve pole rather than sitting on it. Targeting the
         * pole exactly pushes the whole ball to one side and leaves half the
         * frame empty.
         */
        targetY: sign * 0.55,
        /*
         * Wiggle room, not free orbit. The customer can move a few degrees in
         * any direction to look around the fittings while the valve stays the
         * focus — without this the first drag snaps 32 degrees back inside the
         * global tilt clamp and the framing falls apart.
         */
        limits: {
          azCentre: THREE.MathUtils.degToRad(VIEWS.fixings.az),
          azRange: THREE.MathUtils.degToRad(38),
          elevCentre: THREE.MathUtils.degToRad(VIEWS.fixings.elev * sign),
          elevRange: THREE.MathUtils.degToRad(14),
          distMin: dist * 0.78,
          distMax: dist * 1.3,
        },
      });
    } else if (preset === 'technical') {
      /*
       * targetY 0 and a fixed distance: centred, whole, and identical at every
       * size, because a drawing that reframes itself between a 40cm and a 300cm
       * ball is useless for comparing two sheets side by side.
       */
      /*
       * Aimed at the pole, not the ball centre, so the VALVE sits dead centre
       * and the hooks ring it — this is a fitting detail, not a portrait of the
       * ball.
       *
       * The steep elevation is what makes targeting the pole work. The fixings
       * preset warns that aiming at the pole throws the ball to one side and
       * leaves half the frame empty, and at its 52 degrees that is true; from
       * 72 the view looks far enough down the axis that the body falls away
       * symmetrically around the valve instead of off to one edge.
       */
      const sign = this.display === 'hanging' ? 1 : -1;
      const clusterWorld = (4 * mountRadiusCmFor(this.sizeCm)) / this.sizeCm;
      /*
       * 0.40, not the fixings preset's 0.42 nor anything tighter. At 0.62 the
       * cluster does fill the frame, but the ball's silhouette falls outside it
       * entirely and the drawing stops reading as an object at all — just
       * meridians radiating off the edges. The fitting has to be seen ON
       * something.
       */
      const ideal = clusterWorld / (0.40 * 2 * Math.tan(THREE.MathUtils.degToRad(CAM_FOV) / 2));
      this.orbit.goTo('technical', {
        elev: VIEWS.technical.elev * sign,
        // Clamped for the same reason the fixings preset is: unclamped, the
        // cluster rule swings the framing wildly across the size range.
        dist: clamp(ideal, 2.7, 4.4),
        targetY: sign * 0.86,
      });
    } else if (preset === 'back') {
      /*
       * Named explicitly rather than left to the fallback. goTo() looks the
       * preset up in VIEWS itself, but the branch below hardcodes 'default',
       * so any new preset reaching it is silently ignored — which is exactly
       * what happened to 'back' the first time.
       */
      this.orbit.goTo('back', { targetY: VIEWS.back.targetY });
    } else {
      this.orbit.goTo('default', { targetY: VIEWS.default.targetY });
    }
    // Captures ask for this: no tween, no dependence on the render loop.
    if (opts.instant) this.orbit.settle();
    this.invalidate();
  }

  /* ------------------------------------------------------------- ground */

  /*
   * Floor: a procedural grid and a soft contact shadow, both as shaders rather
   * than textures.
   *
   * Procedural because the grid spacing is a live value — it animates whenever
   * the size changes — and rebuilding a repeat-wrapped texture on every frame of
   * that transition would be absurd. fwidth() antialiases the lines, and a
   * radial fade dissolves the plane so it reads as ground rather than as a
   * rectangle floating in white.
   */
  _buildGround() {
    const grp = new THREE.Group();
    const col = new THREE.Color(GROUND.colour);

    this._groundUniforms = {
      uSquare: { value: 1 },
      uFade: { value: GROUND.fadeRadius },
      uColour: { value: col },
      uOpacity: { value: GROUND.gridOpacity },
      uShadowR: { value: 1 },
      uShadowSoft: { value: 0.55 },
      uShadowA: { value: GROUND.shadowOpacity },
    };
    const u = this._groundUniforms;

    const planeGeo = new THREE.PlaneGeometry(
      GROUND.fadeRadius * 2.4,
      GROUND.fadeRadius * 2.4
    );

    const grid = new THREE.Mesh(
      planeGeo,
      new THREE.ShaderMaterial({
        uniforms: { uSquare: u.uSquare, uFade: u.uFade, uColour: u.uColour, uOpacity: u.uOpacity },
        vertexShader: `
varying vec2 vXZ;
void main() {
  vec4 world = modelMatrix * vec4( position, 1.0 );
  vXZ = world.xz;
  gl_Position = projectionMatrix * viewMatrix * world;
}`,
        fragmentShader: `
varying vec2 vXZ;
uniform float uSquare;
uniform float uFade;
uniform vec3 uColour;
uniform float uOpacity;
void main() {
  vec2 g = vXZ / uSquare;
  // Distance to the nearest gridline, in pixels, so the line is one pixel wide
  // at any zoom instead of aliasing into a moire.
  vec2 d = abs( fract( g - 0.5 ) - 0.5 ) / fwidth( g );
  float line = 1.0 - min( min( d.x, d.y ), 1.0 );
  float fade = 1.0 - smoothstep( uFade * 0.30, uFade, length( vXZ ) );
  float a = line * fade * uOpacity;
  if ( a < 0.002 ) discard;
  gl_FragColor = vec4( uColour, a );
}`,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      })
    );
    grid.rotation.x = -Math.PI / 2;
    grid.renderOrder = -2;

    const shadow = new THREE.Mesh(
      planeGeo,
      new THREE.ShaderMaterial({
        uniforms: { uShadowR: u.uShadowR, uShadowSoft: u.uShadowSoft, uShadowA: u.uShadowA },
        vertexShader: `
varying vec2 vXZ;
void main() {
  vec4 world = modelMatrix * vec4( position, 1.0 );
  vXZ = world.xz;
  gl_Position = projectionMatrix * viewMatrix * world;
}`,
        fragmentShader: `
varying vec2 vXZ;
uniform float uShadowR;
uniform float uShadowSoft;
uniform float uShadowA;
void main() {
  float r = length( vXZ ) / max( uShadowR, 1e-4 );
  float a = ( 1.0 - smoothstep( uShadowSoft, 1.0, r ) ) * uShadowA;
  if ( a < 0.002 ) discard;
  gl_FragColor = vec4( 0.0, 0.0, 0.0, a );
}`,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      })
    );
    shadow.rotation.x = -Math.PI / 2;
    // Just above the grid, so the shadow darkens the lines rather than being
    // cut by them.
    shadow.position.y = 0.002;
    shadow.renderOrder = -1;

    grp.add(shadow, grid);
    return grp;
  }

  /*
   * Where the floor sits and how big a square is. Everything here is a function
   * of the PHYSICAL size, which is the whole point — the ball is always radius 1
   * in world units, so the ground is the only thing that can express scale.
   */
  _groundTargets(cm) {
    const hanging = this.display === 'hanging';
    return {
      square: (GROUND.gridCm / cm) * 2 * BALL_RADIUS,
      y: -BALL_RADIUS - (hanging ? GROUND.hangHeight : 0),
      shadowR: BALL_RADIUS * (hanging ? GROUND.hangShadowSpread : 1.12),
      shadowSoft: hanging ? GROUND.hangShadowFade : 0.55,
      shadowA: GROUND.shadowOpacity * (hanging ? 0.55 : 1),
    };
  }

  /*
   * Retarget the ground. `animate` is what turns a size change from a jump cut
   * into visible growth: the ball cannot grow on screen (it is framed to a
   * constant fraction of the viewport at every size), so what the eye actually
   * watches is the ground rescaling underneath it. Motion is a far stronger
   * size cue than comparing two static states, which is why this is animated at
   * all rather than simply set.
   */
  _retargetGround(animate) {
    if (!this.ground) return;
    const t = this._groundTargets(this.sizeCm);
    this._groundTo = t;
    if (!animate || !this._groundFrom) {
      this._groundFrom = t;
      this._groundT = 1;
      this._applyGround(t);
    } else {
      this._groundFrom = this._groundNow || t;
      this._groundT = 0;
      this._groundStart = performance.now();
    }
    this.invalidate();
  }

  _applyGround(v) {
    const u = this._groundUniforms;
    if (!u) return;
    this._groundNow = v;
    u.uSquare.value = v.square;
    u.uShadowR.value = v.shadowR;
    u.uShadowSoft.value = v.shadowSoft;
    u.uShadowA.value = v.shadowA;
    this.ground.position.y = v.y;
  }

  /* --------------------------------------------------------- size change */

  /*
   * Everything that is "the ball and what is welded to it". The ground is
   * pointedly not in this list — it is the ruler, and a ruler that stretches
   * with the thing it measures measures nothing.
   */
  _growTargets() {
    return [this.ball, this.decalMesh, this.fixtures, this._hwOutline];
  }

  _startGrow(fromCm, toCm) {
    if (!this.ball || !fromCm || !toCm || fromCm === toCm) return;

    // Land any grow still in flight before starting another, or a fast run
    // along the size chips leaves the ball stranded at a partial scale.
    this._endGrow(true);

    const swell = clamp(Math.pow(toCm / fromCm, GROW.swellPower), GROW.swellMin, GROW.swellMax);
    this._grow = {
      start: performance.now(),
      swell,
      // The grid is held at `from` through beat one and only travels in beat
      // two — see the GROW comment; this is the part that makes it read as the
      // ball growing rather than the squares shrinking.
      groundFrom: this._groundNow || this._groundTargets(fromCm),
      groundTo: this._groundTargets(toCm),
    };
    this.invalidate();
  }

  /*
   * Land everything on its resting value: scale 1, no dolly, ground on target.
   * `keepGround` is for the restart case, where the next grow is about to take
   * over the ground from wherever it currently is.
   */
  _endGrow(keepGround) {
    const g = this._grow;
    this._grow = null;
    for (const o of this._growTargets()) if (o) o.scale.set(1, 1, 1);
    if (g && !keepGround) this._applyGround(g.groundTo);
  }

  /* Advance the size change. Returns true while it still needs frames. */
  _updateGrow(now) {
    const g = this._grow;
    if (!g) return false;

    const t = now - g.start;
    const total = GROW.growMs + GROW.settleMs;
    if (t >= total) {
      this._endGrow();
      return true; // one last frame to land exactly
    }

    // Ease-in-out cubic, used for both beats so the whole move is one smooth
    // gesture rather than two differently-shaped ones.
    const ease = (u) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);

    let scale;
    if (t < GROW.growMs) {
      // Beat one: the ball swells, ground held.
      scale = 1 + (g.swell - 1) * ease(t / GROW.growMs);
      this._applyGround(g.groundFrom);
    } else {
      /*
       * Beat two: the ball eases back while the ground travels to its new
       * spacing. The ground moving is what reads as the camera pulling back.
       *
       * There is deliberately NO camera dolly here any more. A dolly that
       * starts and ends at zero is non-monotonic by construction, so it pushed
       * the ball briefly SMALLER than its final size before returning — which
       * is what made the move read as up, then down, then up again. Zac, 27
       * Aug. Two movements is the whole animation: up, then back.
       */
      const u = (t - GROW.growMs) / GROW.settleMs;
      const e = ease(u);
      scale = g.swell + (1 - g.swell) * e;
      const a = g.groundFrom;
      const b = g.groundTo;
      const mix = (k) => a[k] + (b[k] - a[k]) * e;
      this._applyGround({
        square: mix('square'),
        y: mix('y'),
        shadowR: mix('shadowR'),
        shadowSoft: mix('shadowSoft'),
        shadowA: mix('shadowA'),
      });
    }

    for (const o of this._growTargets()) if (o) o.scale.set(scale, scale, scale);
    return true;
  }

  /* Advance the grow transition. Returns true while it still needs frames. */
  _updateGround(now) {
    if (!this.ground || this._groundT >= 1) return false;
    const t = clamp((now - this._groundStart) / GROUND.growMs, 0, 1);
    // Ease-out cubic: the change is quickest at the start, which reads as the
    // ground being pushed away rather than as a slider being dragged.
    const e = 1 - Math.pow(1 - t, 3);
    const a = this._groundFrom;
    const b = this._groundTo;
    const mix = (k) => a[k] + (b[k] - a[k]) * e;
    this._applyGround({
      square: mix('square'),
      y: mix('y'),
      shadowR: mix('shadowR'),
      shadowSoft: mix('shadowSoft'),
      shadowA: mix('shadowA'),
    });
    this._groundT = t;
    return true;
  }

  /* ------------------------------------------------- technical line art */

  /*
   * Black-line technical view: the ball and its fixings as a drawing rather
   * than a product shot, for the spec sheet and for anyone checking where the
   * hardware actually lands.
   *
   * Three different techniques, because no single one covers everything and
   * EffectComposer is still ruled out by the no-build-step constraint:
   *
   *   ball silhouette  inverted hull — a slightly larger back-faced black
   *                    sphere showing as a ring outside the white front faces.
   *                    Thickness is in world units, so it survives being
   *                    rendered at 1600px for print instead of thinning to a
   *                    hairline the way gl.LINES would.
   *   seams and cap    real ring geometry on the great circles, same reason.
   *   hardware         EdgesGeometry, where hairline IS the correct weight —
   *                    these are the machined edges of a technical drawing.
   *
   * The artwork decal is deliberately hidden: this diagram is about the product
   * and its fittings, and the hero render already carries the artwork.
   */
  _buildTechnicalOverlay() {
    const grp = new THREE.Group();
    const black = new THREE.MeshBasicMaterial({ color: 0x111111, side: THREE.DoubleSide, toneMapped: false });

    // Silhouette. Sits just proud of the skin so the ring reads at any zoom.
    const hull = new THREE.Mesh(
      new THREE.SphereGeometry(BALL_RADIUS * 1.004, Q.ballSegments[0], Q.ballSegments[1]),
      new THREE.MeshBasicMaterial({ color: 0x111111, side: THREE.BackSide, toneMapped: false })
    );
    grp.add(hull);

    const ring = (radius, tube, rotX, y) => {
      const m = new THREE.Mesh(new THREE.TorusGeometry(radius, tube, 8, 220), black);
      m.rotation.x = rotX;
      m.position.y = y || 0;
      return m;
    };

    const tube = BALL_RADIUS * 0.0016;
    const capLat = Math.PI / 2 - THREE.MathUtils.degToRad(CAP_ANGLE_DEG);
    /*
     * Every drawn line sits fractionally OUTSIDE the skin. Laid exactly on
     * BALL_RADIUS the ring geometry is coplanar with the sphere, and the far
     * half of each ring z-fights its way through to the front — the drawing
     * fills with meridians converging on the hidden pole and stops reading as
     * a solid object at all.
     */
    const LINE_R = BALL_RADIUS * 1.002;

    // Equator, then the two pole-cap boundaries — the panel the fixings sit on.
    grp.add(ring(LINE_R, tube, Math.PI / 2, 0));
    for (const sign of [1, -1]) {
      grp.add(ring(LINE_R * Math.cos(capLat), tube, Math.PI / 2, sign * LINE_R * Math.sin(capLat)));
    }

    /*
     * Gore seams. Half-circles from pole to pole, one per gore boundary. Gore
     * centres sit on u = k/N, so the boundaries fall halfway between — the same
     * convention the seam normal map uses, or the drawn seams would not line up
     * with the rendered ones.
     */
    const gores = this.gores || goresForSize(this.sizeCm);
    for (let k = 0; k < gores; k++) {
      /*
       * A torus is born in the XY plane, which already contains the Y axis and
       * therefore both poles — so it IS a meridian, and the only transform it
       * needs is a spin about Y to its longitude. Tipping it about X or Z turns
       * it into a circle of latitude that projects as a spoke through the
       * centre of the disc, which is emphatically not a seam.
       */
      const meridian = new THREE.Mesh(new THREE.TorusGeometry(LINE_R, tube, 8, 220), black);
      meridian.rotation.y = ((k + 0.5) / gores) * Math.PI * 2;
      grp.add(meridian);
    }

    return grp;
  }

  /*
   * Swap the whole scene between the product render and the drawing. Every
   * material is stashed on the mesh itself rather than in a parallel list, so a
   * rebuild of the fixings between toggles cannot desynchronise the two.
   */
  setTechnical(on) {
    const want = !!on;
    if (want === !!this._technical) return;
    this._technical = want;

    if (want) {
      this._techSaved = {
        environment: this.scene.environment,
        background: this.scene.background,
        decalVisible: this.decalMesh ? this.decalMesh.visible : false,
      };
      this.scene.environment = null;
      this.scene.background = new THREE.Color(0xffffff);
      if (this.decalMesh) this.decalMesh.visible = false;
      // A technical elevation has no floor under it.
      if (this.ground) {
        this._techSaved.groundVisible = this.ground.visible;
        this.ground.visible = false;
      }

      // toneMapped:false or ACES renders "white" as ~0.92 grey and the drawing
      // comes out on a grey field instead of paper white.
      this._techWhite = this._techWhite || new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
      this._techEdges = new THREE.Group();

      /*
       * Without this the fixings' world matrices are still identity — they are
       * only refreshed during a render — so every edge line collapses to the
       * ball centre and the hardware silently vanishes from the drawing.
       */
      this.scene.updateMatrixWorld(true);

      const edgeMat = new THREE.LineBasicMaterial({ color: 0x111111, toneMapped: false });
      for (const root of [this.ball, this.fixtures]) {
        if (!root) continue;
        root.traverse((o) => {
          if (!o.isMesh || !o.geometry) return;
          // Surface decoration is hidden outright rather than merely left
          // un-outlined, or its white body still occludes what sits behind it.
          if (o.userData.sbDetailOnly) {
            o.userData._sbHidden = o.visible;
            o.visible = false;
            return;
          }
          o.userData._sbMat = o.material;
          o.material = this._techWhite;
          // The ball gets a hull instead — EdgesGeometry on a smooth sphere
          // returns nothing, since no adjacent faces meet above the threshold.
          if (o === this.ball) return;
          const eg = new THREE.EdgesGeometry(o.geometry, 22);
          const ls = new THREE.LineSegments(eg, edgeMat);
          o.matrixWorld.decompose(ls.position, ls.quaternion, ls.scale);
          this._techEdges.add(ls);
        });
      }

      this._techOverlay = this._buildTechnicalOverlay();
      this.scene.add(this._techOverlay);
      this.scene.add(this._techEdges);
    } else {
      for (const root of [this.ball, this.fixtures]) {
        if (!root) continue;
        root.traverse((o) => {
          if (o.isMesh && o.userData._sbMat) {
            o.material = o.userData._sbMat;
            delete o.userData._sbMat;
          }
          if (o.userData._sbHidden !== undefined) {
            o.visible = o.userData._sbHidden;
            delete o.userData._sbHidden;
          }
        });
      }
      if (this._techOverlay) {
        this.scene.remove(this._techOverlay);
        this._techOverlay.traverse((o) => o.geometry && o.geometry.dispose());
        this._techOverlay = null;
      }
      if (this._techEdges) {
        this.scene.remove(this._techEdges);
        this._techEdges.traverse((o) => o.geometry && o.geometry.dispose());
        this._techEdges = null;
      }
      const saved = this._techSaved || {};
      this.scene.environment = saved.environment || this.envTexture || null;
      this.scene.background = saved.background || null;
      if (this.decalMesh) this.decalMesh.visible = !!saved.decalVisible;
      if (this.ground && saved.groundVisible !== undefined) this.ground.visible = saved.groundVisible;
      this._techSaved = null;
    }
    this.invalidate();
  }

  /*
   * One-shot technical render. Toggles in, renders, toggles back, so the live
   * preview is never left in drawing mode if a caller forgets.
   */
  async renderTechnical(pxSize) {
    const was = !!this._technical;
    this.setTechnical(true);
    let out;
    try {
      out = await this.renderExport(pxSize);
    } finally {
      this.setTechnical(was);
    }
    return out;
  }

  /*
   * Largest logo size, as a percentage of diameter, whose top edge still
   * clears the pole cap. Artwork is locked to the equator, so a logo whose
   * width is p% of the diameter spans an arc of 2*(p/100) radians, i.e. it
   * reaches (p/100) radians of latitude either side.
   */
  maxLogoPct() {
    const capLimit = Math.round((Math.PI / 2 - THREE.MathUtils.degToRad(CAP_ANGLE_DEG)) * 100);
    return Math.min(capLimit, LOGO_MAX_PCT_PROVISIONAL);
  }

  /*
   * Offscreen render at pxSize squared, returned as a fresh 2D canvas so
   * callers can treat it exactly like the old 2D export.
   */
  async renderExport(pxSize) {
    // A mockup or a spec sheet showing a ball caught mid-swell would be a
    // subtly wrong record of what was ordered.
    this._endGrow();
    const px = Math.max(1, Math.round(pxSize || 1600));
    const prevRatio = this.renderer.getPixelRatio();

    this.renderer.setPixelRatio(1);
    this.renderer.setSize(px, px, false);
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
    this.orbit.applyTo(this.camera);
    this.renderer.render(this.scene, this.camera);

    const out = document.createElement('canvas');
    out.width = px;
    out.height = px;
    out.getContext('2d').drawImage(this.renderer.domElement, 0, 0, px, px);

    // Restore the live view in the same frame so nothing flickers. _resize()
    // recomputes the backing store from the CSS box, so it is the whole restore.
    this.renderer.setPixelRatio(prevRatio);
    this._resize();
    this.orbit.applyTo(this.camera);
    this.renderer.render(this.scene, this.camera);

    return out;
  }

  dispose() {
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._ro) this._ro.disconnect();
    if (this.orbit) this.orbit.dispose();
    this.scene?.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) {
        for (const m of [].concat(o.material)) {
          for (const k of Object.keys(m)) {
            if (m[k] && m[k].isTexture) m[k].dispose();
          }
          m.dispose();
        }
      }
    });
    this.envTexture?.dispose();
    this.renderer?.dispose();

    /*
     * Clear the caches too. The traverse above disposes the seam normal maps
     * and hardware materials because they are reachable from the scene, so
     * leaving them cached would hand a disposed texture to the next init.
     */
    for (const tex of seamMapCache.values()) tex.dispose();
    seamMapCache.clear();
    this._hwMats = null;
    this.fixtures = null;
    this.envTexture = null;
    this.envReady = false;
    this.ready = false;
  }
}

const scene = new SB3DScene();

window.SB3D = {
  init: (canvas, opts) => scene.init(canvas, opts),
  setSize: (cm) => scene.setSize(cm),
  setColour: (name, secondary) => scene.setColour(name, secondary),
  /*
   * Both arguments, deliberately. Dropping opts here silently pinned
   * artTwoSided to false, so "Front and back" never rendered the second decal —
   * the facade-wrapper gotcha, for the third time on this file.
   */
  setArtwork: (img, opts) => scene.setArtwork(img, opts),
  /*
   * opts carries the slot. Forwarded rather than dropped — the first version of
   * the facade swallowed setArtwork's second argument, which silently disabled
   * two-sided print for weeks. Same shape, same trap.
   */
  setArtworkTransform: (t, opts) => scene.setArtworkTransform(t, opts),
  setFixings: (f) => scene.setFixings(f),
  /*
   * Addition to the brief's interface. The fixing count is derived from size in
   * exactly one place — here — and the configurator needs to read it back for
   * the on-page note, the spec text and the cart line. The alternative is
   * duplicating the size thresholds in two files, which would drift.
   */
  fixingSummary: () => scene.fixingSummary(),
  setView: (p, opts) => scene.setView(p, opts),
  maxLogoPct: () => scene.maxLogoPct(),
  renderExport: (px) => scene.renderExport(px),
  /* Black-line technical drawing — the spec sheet's fixing diagram, and a view
   * the customer can toggle. setTechnical holds the mode on the live canvas;
   * renderTechnical is the one-shot that always restores it. */
  /* The active quality tier, so the commercial layer can size its exports to
   * the same budget rather than keeping a second copy of the decision. */
  quality: () => ({ ...Q }),
  setTechnical: (on) => scene.setTechnical(on),
  renderTechnical: (px) => scene.renderTechnical(px),
  dispose: () => scene.dispose(),
};

export default window.SB3D;
